// Ensure Intl.PluralRules is available before i18next initializes (older Safari/iOS)
import '@formatjs/intl-pluralrules/polyfill.js';
import '@formatjs/intl-pluralrules/locale-data/en.js';
import '@formatjs/intl-pluralrules/locale-data/it.js';
import '@formatjs/intl-pluralrules/locale-data/es.js';

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Get saved language from localStorage or default to English
// (geo detection will override to Italian for Italian IPs)
const getSavedLanguage = () => {
  try {
    // First check for user-specific language
    const userLang = localStorage.getItem('betterhusbandUserLang');
    if (userLang === 'en' || userLang === 'it' || userLang === 'es') {
      console.log(`[i18n] Found user language in localStorage: ${userLang}`);
      return userLang;
    }

    // Then check for general i18next language
    const i18nextLang = localStorage.getItem('i18nextLng');
    if (i18nextLang === 'en' || i18nextLang === 'it' || i18nextLang === 'es') {
      console.log(`[i18n] Found i18next language in localStorage: ${i18nextLang}`);
      return i18nextLang;
    }

    // Default to English
    console.log('[i18n] No saved language found, defaulting to English');
    return 'en';
  } catch (error) {
    console.warn('Error reading language from localStorage:', error);
    return 'en';
  }
};

const resources = {
  it: {
        common:{
          saving: "Salvataggio in corso...",
          save: "Salva",
          cancel: "Annulla"
        },
    translation: {
      language: "it",
      meta: {
        title: "Better Husband — Suggerimenti quotidiani per migliorare la tua relazione",
        description: "Suggerimenti quotidiani, personalizzati per rafforzare la tua relazione."
      },
      splash: {
        title: "Piccole azioni quotidiane.\nGrandi differenze in coppia.",
        start: "Inizia"
      },
      welcome: {
        problem: "Non è che non vuoi fare abbastanza.\nÈ che nessuno ti ha mai detto come farlo.",
        solution: "Un suggerimento al giorno, personalizzato per la tua relazione.",
        tone: "Niente giudizi. Niente voti. Solo aiuto pratico.",
        continue: "Continua"
      },
      onboarding: {
        title: "Conosciamoci",
        subtitle: "Non esistono risposte giuste o sbagliate",
        step1: {
          title: "Cominciamo",
          name: "Come ti chiami?",
          namePlaceholder: "Il tuo nome",
          birthDate: "Quando sei nato?",
          timezone: "In che fuso orario ti trovi?",
          language: "Lingua preferita",
          italian: "Italiano",
          english: "English"
        },
        step2: {
          title: "La tua relazione",
          type: "Stato attuale",
          cohabitating: "Conviventi",
          married: "Sposati",
          duration: "Da quanto tempo state insieme?",
          durationLessThan1: "Meno di 1 anno",
          duration1to2: "1-2 anni",
          duration2to5: "2-5 anni",
          duration5to10: "5-10 anni",
          duration10plus: "Più di 10 anni",
          children: "Avete figli?",
          yes: "Sì",
          no: "No",
          addChild: "Aggiungi figlio",
          removeChild: "Rimuovi",
          childAge: "Età figlio",
          ageLessThan1: "Meno di 1 anno",
          age1: "1 anno",
          age2: "2 anni",
          age3: "3 anni",
          age4: "4 anni",
          age5: "5 anni",
          age6: "6 anni",
          age7: "7 anni",
          age8: "8 anni",
          age9: "9 anni",
          age10: "10 anni",
          age11: "11 anni",
          age12: "12 anni",
          age13: "13 anni",
          age14: "14 anni",
          age15: "15 anni",
          age16: "16 anni",
          age17: "17 anni",
          age18plus: "18+ anni"
        },
        step3: {
          title: "Vita quotidiana",
          communication: "Come comunicate di solito?",
          commLow: "Parliamo poco",
          commMedium: "Parliamo abbastanza",
          commHigh: "Parliamo molto",
          stress: "Livello di stress percepito",
          stressLow: "Basso",
          stressMedium: "Medio",
          stressHigh: "Alto",
          intimacy: "Livello di intimità",
          intimacyLow: "Bassa",
          intimacyMedium: "Media",
          intimacyHigh: "Alta"
        },
        step4routine: {
          title: "La Vostra Routine",
          subtitle: "Parliamo della vostra vita quotidiana",
          workSchedule: "I tuoi orari di lavoro",
          selectOption: "Seleziona...",
          fullTime: "Tempo pieno (ufficio)",
          partTime: "Part-time",
          freelance: "Libero professionista",
          remote: "Smart working",
          shiftWork: "Turni",
          notWorking: "Non lavoro",
          timeTogether: "Quanto tempo passate insieme ogni giorno?",
          timePoco: "Poco",
          timeMedio: "Medio",
          timeMolto: "Molto",
          householdManagement: "Chi gestisce più spesso casa/figli?",
          householdMainlyMe: "Principalmente io",
          householdMainlyPartner: "Principalmente il/la partner",
          householdBalanced: "Equilibrato"
        },
        step5tension: {
          title: "Cosa Crea Tensione",
          subtitle: "Seleziona le principali fonti di tensione (max 2)",
          communication: "Comunicazione",
          time_together: "Tempo insieme",
          home_organization: "Casa / Organizzazione",
          children: "Figli",
          intimacy: "Intimità",
          work_stress: "Lavoro / Stress",
          helpText: "Serve solo a capire come aiutarti meglio"
        },
        step6partner: {
          title: "Il Nome del Tuo Partner",
          subtitle: "Come si chiama la persona speciale della tua vita?",
          partnerName: "Nome del partner",
          partnerNamePlaceholder: "es. Maria, Giovanni, Sofia..."
        },
        step6terms: {
          title: "Ultimo Passo",
          subtitle: "Prima di iniziare, leggi e accetta le nostre policy",
          acceptTerms: "Ho letto e accetto i",
          termsLink: "Termini di Servizio",
          acceptPrivacy: "Ho letto e accetto la",
          privacyLink: "Privacy Policy",
          disclaimer: "Puoi ritirare il consenso in qualsiasi momento dalle impostazioni"
        },
        step6: {
          title: "Preferenze",
          tone: "Che tono preferisci per i suggerimenti?",
          gentle: "Gentile e incoraggiante",
          direct: "Diretto e pratico",
          ironic: "Ironico e leggero",
          reminderTime: "A che ora preferisci il promemoria?",
          availability: "Quanti minuti hai al giorno per questi suggerimenti?",
          minutes: "minuti"
        },
        step4: {
          title: "Preferenze",
          tone: "Che tono preferisci per i suggerimenti?",
          gentle: "Gentile e incoraggiante",
          direct: "Diretto e pratico",
          ironic: "Ironico e leggero",
          reminderTime: "A che ora preferisci il promemoria?",
          availability: "Quanti minuti hai al giorno per questi suggerimenti?",
          minutes: "minuti"
        },
        next: "Avanti",
        back: "Indietro",
        finish: "Completa"
      },
      onboardingV2: {
        title: "Benvenuto",
        subtitle: "Cominciamo.",
        step0: {
          title: "Oggi.",
          body: "Un piccolo gesto. È sufficiente."
        },
        step1: {
          title: "Chi c'è in casa?",
          micro: "Puoi cambiare questa impostazione in qualsiasi momento.",
          none: "Solo noi due",
          young: "Figli piccoli",
          older: "Figli più grandi",
          mixed: "Misto"
        },
        step2: {
          title: "Su cosa deve concentrarsi?",
          micro: "Non è un impegno. Solo una direzione.",
          partner: "La mia partner",
          kids: "I miei figli",
          both: "Entrambi"
        },
        step3: {
          title: "Quanto tempo hai realisticamente la maggior parte dei giorni?",
          micro: "Sii onesto.",
          five: "5 minuti",
          ten: "10 minuti",
          twenty: "20 minuti"
        },
        step4: {
          title: "Vuoi un promemoria?",
          yes: "Sì, quando aiuta",
          no: "No, aprirò l'app",
          micro: "Nessuna pressione."
        },
        step5: {
          title: "Vuoi aggiungere il suo nome?",
          placeholder: "Nome (opzionale)",
          micro: "Rimane privato."
        },
        stepTension: {
          title: "Cosa crea più tensione?",
          micro: "Scegli fino a 2. Serve solo a capire come aiutarti.",
          communication: "Comunicazione",
          time_together: "Tempo insieme",
          home_organization: "Casa / Organizzazione",
          children: "Figli",
          intimacy: "Intimità",
          work_stress: "Lavoro / Stress"
        },
        finish: "Inizia",
        next: "Continua",
        transition: "Stiamo configurando la tua esperienza…"
      },
      dashboard: {
        title: "Oggi",
        loading: "Generazione suggerimento...",
        mainSuggestion: "Suggerimento del giorno",
        microAction: "Micro-azione veloce",
        estimatedTime: "Tempo stimato",
        minutes: "min",
        done: "Fatto",
        postponed: "Dopo",
        notDone: "Salta",
        later: "Dopo",
        skip: "Prossimo",
        next: "Prossimo",
        feedbackSuccess: "Grazie per il feedback!",
        newSuggestion: "Nuovo suggerimento generato!",
        addNotes: "Aggiungi note (opzionale)",
        notesTitle: "Note",
        notesSubtitle: "Aggiungi note su come è andata",
        notesPlaceholder: "es. Le piacerebbe andare a teatro, ha menzionato un libro che vorrebbe leggere...",
        complete: "Completa",
        cancel: "Annulla",
        testModeTitle: "Modalità di test: genera un nuovo suggerimento",
        noSuggestionYet: "Suggerimento non ancora disponibile",
        suggestionComesAt: "Un nuovo suggerimento sarà generato all'ora del tuo promemoria.",
        generateManually: "Genera manualmente",
        premiumLocked: {
          noSuggestions: "Nessun suggerimento",
          cta: "Abbonati per sbloccare il tuo piano"
        },
        congratulations: [
          "È tutto per oggi! Sei un grande! 🌟",
          "Fantastico! Continua così! 💪",
          "Ottimo lavoro! Sei sulla strada giusta! 🎯",
          "Eccellente! Ogni piccolo gesto conta! ❤️",
          "Perfetto! Lei apprezzerà sicuramente! 💝",
          "Grande! Questi gesti fanno crescere l'amore! 🌱",
          "Complimenti! Stai costruendo qualcosa di bello! 🏆",
          "Ben fatto! La costanza è la chiave! 🔑",
          "Straordinario! Ogni giorno sei un marito migliore! 🌈"
        ],
        category: {
          husband: "Marito",
          father: "Papà",
          partner: "Partner"
        },
        difficulty: {
          low: "Facile",
          medium: "Medio",
          high: "Impegnativo"
        },
        context: {
          anytime: "Quando vuoi",
          morning: "Mattina",
          afternoon: "Pomeriggio",
          evening: "Sera"
        },
        alternative: {
          title: "Alternativa",
          question: "Serve qualcosa di diverso?",
          description: "Posso proporti un'altra idea personalizzata per oggi.",
          noThanks: "No, grazie",
          seeAlternative: "Dammi un'alternativa",
          generating1: "D'accordo. Cerco per te la prossima azione...",
          generating2: "Analizzo le tue preferenze...",
          generating3: "Genero un'alternativa personalizzata...",
          generating4: "Quasi pronto!",
          success: "Ecco un'altra idea!",
          error: "Errore nel caricamento dell'alternativa"
        },
        alternativePaywall: {
          title: "Non tutti i giorni sono uguali.",
          description: "Con Premium puoi adattare il gesto al momento in cui ti trovi.",
          cta: "Attiva Precisione",
          continue: "Continua così"
        },
        skip: {
          notToday: "Non oggi",
          dontFeelLikeIt: "Non mi va",
          revert: "Fallo comunque",
          banner5: "Son 5 giorni che passi. Oggi è una buona occasione.",
          banner10: "Non serve molto. Solo un gesto."
        },
        precision: {
          title: "Serve qualcosa di più adatto?",
          description: "Con Premium il gesto si adatta alla tua giornata.",
          cta: "Attiva Precisione",
          close: "Chiudi"
        },
        premiumPrecision: {
          title: "Meglio, non di più.",
          description: "Premium adatta il gesto a energia, stress e costanza.",
          cta: "Attiva Precisione",
          close: "Non ora"
        },
        completion: {
          phrases: [
            "Bravo! Hai fatto la differenza oggi 💚",
            "Grande! Un gesto che conta 🌱",
            "Ce l'hai fatta! Stai crescendo 🌳",
            "Perfetto. Lei lo noterà ✨",
            "Ogni gesto ti avvicina 💪",
            "Bellissimo. Continua così! 🔥",
            "Fatto! Il tuo giardino cresce 🌿",
            "Sei stato presente. È tutto. 💚"
          ],
          addNote: "Aggiungi nota",
          noteTitle: "Nota",
          noteSubtitle: "Una riga, se vuoi.",
          notePlaceholder: "Scrivi una nota...",
          noteClose: "Chiudi",
          noteSave: "Salva",
          error: "Errore. Riprova.",
          slowConnection: "Connessione lenta"
        },
        forest: {
          label: "La tua foresta"
        }
      },
      profile: {
        title: "Tu",
        settings: "Impostazioni",
        history: "Storico",
        language: "Lingua",
        reminderTime: "Orario promemoria",
        tone: "Tono suggerimenti",
        save: "Salva",
        saved: "Profilo aggiornato!",
        personalInfo: "Informazioni personali",
        name: "Nome",
        email: "Email",
        birthDate: "Data di nascita",
        timezone: "Fuso orario",
        yourSituation: "La tua situazione",
        homeMembers: "Chi c'è in casa",
        whatMatters: "Cosa conta di più",
        timeAvailable: "Tempo disponibile",
        edit: "Modifica",
        editYourData: "Modifica i tuoi dati",
        updateYourProfile: "Aggiorna il tuo profilo",
        back: "Indietro",
        kids_cluster: {
          // match onboardingV2.step1 options so profile page uses the same wording
          none: "Solo noi due",
          young: "Figli piccoli",
          older: "Figli più grandi",
          mixed: "Misto"
        },
        focus: {
          partner: "Partner",
          self: "Te stesso",
          family: "Famiglia"
        },
        relationship: {
          cohabitating: "Conviventi",
          married: "Sposati"
        }
      },
      history: {
        title: "Storico",
        empty: "Nessun suggerimento ancora",
        emptyEncouragement: "Ogni viaggio inizia con un piccolo passo.",
        emptyIcon: "trending-up",
        completed: "Completati",
        total: "Totale",
        status: {
          done: "Fatto",
          postponed: "Rimandato",
          not_done: "Non fatto",
          pending: "In attesa"
        }
      },
      auth: {
        login: "Accedi",
        register: "Registrati",
        loginButton: "Accedi",
        registerButton: "Crea Account",
        name: "Nome",
        namePlaceholder: "Il tuo nome",
        emailPlaceholder: "tua@email.com",
        tagline: "Piccole azioni quotidiane. Grandi differenze in coppia.",
        loginSuccess: "Accesso effettuato!",
        registerSuccess: "Account creato con successo!",
        loginError: "Email o password non validi",
        registerError: "Errore durante la registrazione",
        noAccount: "Non hai un account? Registrati sopra",
        hasAccount: "Hai già un account? Accedi sopra"
      },
      nav: {
        home: "Home",
        profile: "Tu",
        history: "Storico",
        notes: "Note",
        events: "Eventi",
        stats: "Presenza"
      },
      events: {
        title: "Eventi Speciali",
        eventAdded: "Evento aggiunto!",
        eventDeleted: "Evento eliminato",
        eventType: "Tipo evento",
        anniversary: "Anniversario",
        birthday: "Compleanno",
        vacation: "Vacanza",
        custom: "Personalizzato",
        eventTitle: "Titolo",
        titlePlaceholder: "es. Anniversario di matrimonio",
        eventDate: "Data",
        date: "Data",
        recurring: "Ripeti",
        recurrence_none: "Nessuna ricorrenza",
        recurrence_daily: "Giornaliera",
        recurrence_weekly: "Settimanale",
        recurrence_monthly: "Mensile",
        recurrence_yearly: "Annuale",
        notes: "Note",
        save: "Salva",
        cancel: "Annulla",
        empty: "Nessun evento ancora",
        emptyEncouragement: "I momenti importanti non sempre si annunciano.",
        emptyIcon: "calendar",
        today: "Oggi",
        back: "Indietro",
        eventUpdated: "Evento aggiornato"
      },
      stats: {
        title: "Presenza",
        days: "giorni",
        present: "Presente.",
        lastDays: "Ultimi {{count}} giorni",
        daysOfPresence: "{{count}} / {{total}} giorni presenti",
        presenceDescription: "Conta solo quando segni Done.",
        timeline: "Timeline",
        done: "Fatto",
        skipped: "Saltato",
        missed: "Mancato",
        categories: "Dove ti sei presentato",
        timeInvested: "Tempo Investito",
        hoursTotal: "ore totali",
        notes: "Note",
        precision: "Precisione",
        categoryBreakdown: "Suddivisione per Categoria",
        timeMetrics: "Metriche Temporali",
        avgMinutes: "Minuti medi",
        weeklyAvg: "Media settimanale",
        consistencyScore: "Punteggio Consistenza",
        activeAdaptation: "Adattamento attivo",
        adaptationText: "La sera ti riesce meglio.\nI gesti brevi li completi di più.",
        timeSection: "Tempo",
        timeDescription: "~{{hours}}h negli ultimi {{days}} giorni",
        overview: "PANORAMICA",
        starting: "Inizio.",
        growing: "Crescita.",
        reliable: "Affidabile.",
        unwavering: "Incrollabile.",
        dotCaption: "Ogni punto è una scelta. Ogni scelta costruisce presenza.",
        gesturesMade: "{{count}} gesti fatti.",
        dotDone: "FATTO",
        dotSkipped: "SALTATO",
        dotEmpty: "—",
        dotNoDetail: "Gesto completato.",
        dotSkippedDetail: "Hai saltato questo giorno.",
        dotEmptyDetail: "Nessun gesto per questo giorno.",
        streakLegendary: "Leggendario",
        streakChampion: "Campione",
        streakOnFire: "On Fire",
        streakExcellent: "Eccellente",
        streakStart: "Inizio",
      },
      metrics: {
        title: "Metriche",
        week: "Settimana",
        month: "Mese",
        all: "Tutto",
        engagementScore: "Punteggio Coinvolgimento",
        completionRate: "Tasso Completamento",
        currentStreak: "Serie Attuale",
        best: "Migliore",
        actionsDone: "Azioni Fatte",
        assigned: "assegnate",
        timeInvested: "Tempo Investito",
        checkinsAndMood: "Check-in e Umore",
        totalCheckins: "Check-in totali",
        avgMood: "Umore medio",
        moodTrend: "Andamento Umore",
        byCategory: "Per Categoria",
        weeklyBreakdown: "Andamento Settimanale",
        skippedActions: "Azioni Saltate",
        skippedDesc: "Nessun problema, ogni giorno e' nuovo",
        noData: "Ancora nessun dato",
      },
      categories: {
        presence: "Presenza",
        repair: "Casa",
        logistics: "Logistica",
        connection: "Connessione",
        play: "Bambini",
        change_of_scene: "Cambio di Scena",
        other: "Altro",
        husband: "Marito",
        father: "Padre"
      },
      menu: {
        title: "Menu",
        profile: "Tu",
        game: "Husband Simulator (gioco)",
        metrics: "Metriche",
        settings: "Impostazioni",
        subscription: "Abbonamento",
        logout: "Disconnetti",
        privacy: "Privacy",
        terms: "Termini e Condizioni",
        user: "Utente"
      },
      notes: {
        title: "Note",
        search: "Cerca note...",
        newNote: "Nuova nota",
        newReflection: "Nuova riflessione",
        journeyTitle: "Il viaggio finora.",
        journeySubtitle: "Ogni nota è un passo verso più presenza e chiarezza.",
        today: "OGGI",
        daysAgo: "{{count}} GIORNO FA",
        daysAgo_plural: "{{count}} GIORNI FA",
        titlePlaceholder: "Titolo",
        contentPlaceholder: "Scrivi qui la tua nota...",
        cancel: "Annulla",
        create: "Crea",
        save: "Salva",
        back: "Indietro",
        noContent: "Nessun contenuto",
        empty: "Nessuna nota ancora. Crea la tua prima nota!",
        emptyEncouragement: "Alcuni momenti meritano di essere ricordati.",
        emptyIcon: "book-open",
        noResults: "Nessun risultato trovato",
        created: "Nota creata!",
        updated: "Nota aggiornata!",
        deleted: "Nota eliminata!",
        error: "Errore durante l'operazione",
        titleRequired: "Il titolo è obbligatorio",
        lastUpdated: "Ultimo aggiornamento"
      },
      settings: {
        title: "Impostazioni",
        language: "Lingua",
        notifications: "Notifiche",
        notificationsDesc: "Ricevi promemoria giornalieri",
        reminderTime: "Orario promemoria",
        adminProgQuestions: "Domande progressive",
        adminProgQuestionsClicked: "Test amministratore attivato",
        saved: "Impostazioni salvate!",
        error: "Errore nel salvataggio",
        pushNotifications: "Notifiche push",
        pushEnabledDesc: "Riceverai notifiche anche quando l'app è chiusa",
        pushDisabledDesc: "Attiva per ricevere promemoria",
        pushDeniedDesc: "Permesso negato. Abilita dalle impostazioni del browser",
        pushEnabled: "Notifiche push attivate!",
        pushDisabled: "Notifiche push disattivate",
        pushDenied: "Permesso per le notifiche negato",
        pushManagedNote: "Le notifiche push sono gestite dal dispositivo — usa le impostazioni del tuo telefono.",
        progressive: {
          modalTitle: "Quick question",
          partner_name: {
            title: "Vuoi aggiungere il suo nome?",
            placeholder: "Nome (opzionale)"
          },
          number_of_children: {
            title: "Quanti figli avete?",
            options: {
              "1": "1",
              "2": "2",
              "3": "3",
              "4+": "4+"
            }
          },
          schedule_type: {
            title: "Com'è la tua giornata ultimamente?",
            options: {
              predictable: "Prevedibile",
              chaotic: "Caotica",
              shifts: "Turni"
            }
          },
          together_time: {
            title: "Ultimamente, riuscite a passare tempo insieme?",
            options: {
              low: "Basso",
              medium: "Medio",
              high: "Alto"
            }
          },
          home_load: {
            title: "A casa, chi si occupa maggiormente dei compiti?",
            options: {
              me: "Io",
              partner: "Partner",
              even: "Equamente",
              varies: "Varia"
            }
          },
          focus_tension: {
            title: "Su quale ambito ti piacerebbe concentrarti questo mese?",
            options: {
              communication: "Comunicazione",
              time_together: "Tempo insieme",
              home_logistics: "Casa / Logistica",
              work_stress: "Lavoro / Stress",
              intimacy: "Intimità",
              parenting: "Genitorialità"
            }
          }
        },
        dailyReminder: "Promemoria giornaliero",
        dailyReminderDesc: "Ricevi una notifica con il suggerimento del giorno",
        eventReminders: "Promemoria eventi",
        eventRemindersDesc: "Ricevi notifiche per gli eventi speciali",
        checkinTime: "Orario check-in serale",
        checkinTimeDesc: "Quando vuoi ricevere il promemoria per il check-in",
        preferences: "Preferenze",
        tone: "Tono suggerimenti",
        toneGentle: "Gentile",
        toneDirect: "Diretto",
        toneIronic: "Ironico",
        availability: "Tempo disponibile",
        minutesDay: "min/giorno",
        partner: "Partner",
        partnerName: "Nome del partner",
        partnerNamePlaceholder: "es. Maria, Giovanni...",
        account: "Account",
        editProfile: "Modifica profilo",
        subscription: "Abbonamento",
        freePlan: "Piano gratuito",
        dangerZone: "Zona pericolosa",
        deleteAccount: {
          title: "Elimina account",
          description: "Questa azione è permanente e irreversibile",
          confirmMessage: "Sei sicuro? Tutti i tuoi dati verranno eliminati definitivamente.",
          confirm: "Elimina",
          cancel: "Annulla",
          deleting: "Eliminazione...",
          success: "Account eliminato con successo",
          error: "Errore nell'eliminazione dell'account"
        },
        about: "Informazioni",
        privacy: "Privacy",
        terms: "Termini e condizioni",
        version: "Versione"
      },
      subscription: {
        title: "Abbonamento",
        back: "Indietro",
        returnedFromPortal: "Bentornato!",
        free: "Gratuito",
        premium: "Premium",
        freePrice: "Gratis",
        noSubscription: "Nessun abbonamento",
        noSubscriptionDesc: "Per accedere alle funzionalità dell'app serve l'accesso a vita",
        inactive: "Inattivo",
        dismiss: "Chiudi",
        dismissRequiredAccess: "Chiudi schermata abbonamento",
        subscribe: "Abbonati",
        startTrial: "Inizia prova gratuita di 7 giorni",
        recommended: "Consigliato",
        current: "Attuale",
        currentPlan: "Piano attuale",
        upgrade: "Attiva Precisione",
        management: "Gestione",
        managePayment: "Gestisci pagamento",
        managePaymentDesc: "Modifica metodo di pagamento",
        billingHistory: "Storico fatture",
        billingHistoryDesc: "Visualizza fatture passate",
        cancelSubscription: "Annulla abbonamento",
        cancelDesc: "Annulla il rinnovo automatico",
        infoTitle: "Informazioni",
        infoPremium: "Il tuo abbonamento si rinnova automaticamente. Puoi annullarlo in qualsiasi momento.",
        infoFree: "Per accedere alle funzionalità dell'app serve l'accesso a vita. Un pagamento unico, poi è tuo per sempre.",
        managingRedirect: "Apertura portale di gestione...",
        errorPortal: "Errore nell'apertura del portale di gestione",
        noCustomerId: "Abbonamento non gestibile. Contatta il supporto.",
        manualPremiumTitle: "Abbonamento Premium Attivo",
        manualPremiumDesc: "Il tuo account è Premium. Gestisci l'abbonamento, cambia piano o disdici direttamente dallo store da cui l'hai acquistato.",
        nextBilling: "Prossimo addebito",
        trialEndsOn: "Il periodo di prova termina il",
        manageOnGoogle: "Gestisci su Google Play",
        manageOnGoogleDesc: "Apre Google Play › Abbonamenti",
        manageOnStripe: "Gestisci abbonamento",
        manageOnStripeDesc: "Apri il portale di fatturazione per aggiornare il pagamento o annullare",
        manageOnApple: "Gestisci negli Abbonamenti Apple",
        manageOnAppleDesc: "Apre Impostazioni › ID Apple › Abbonamenti",
        storeManagedDescGoogle: "Il tuo abbonamento è gestito da Google Play. Cambia piano, aggiorna il metodo di pagamento o disdici dal tuo account Google.",
        storeManagedDescApple: "Il tuo abbonamento è gestito da Apple. Cambia piano, aggiorna il metodo di pagamento o disdici dal tuo account Apple.",
        storeManagedDescWeb: "Se ti sei abbonato sul web, gestisci l'abbonamento qui sotto. Se ti sei abbonato dall'app mobile, usa Google Play o l'App Store sul telefono.",
        webNotAvailable: "Gli abbonamenti sono disponibili solo nell'app mobile BetterHusband. Installala dall'App Store o da Google Play.",
        feature1Free: "1 gesto al giorno",
        feature2Free: "Note sul partner",
        feature3Free: "Promemoria eventi speciali",
        feature1Premium: "Contesto profondo & apprendimento progressivo",
        feature2Premium: "Gesto alternativo",
        feature3Premium: "Check-in serale"
      },
      privacy: {
        title: "Privacy",
        heading: "Privacy Policy",
        lastUpdated: "Ultimo aggiornamento: Dicembre 2024",
        section1Title: "Raccolta dati",
        section1Content: "Raccogliamo solo i dati necessari per fornirti suggerimenti personalizzati. I tuoi dati sono crittografati e protetti.",
        section2Title: "Uso dei dati",
        section2Content: "I tuoi dati vengono utilizzati esclusivamente per generare suggerimenti personalizzati e migliorare la tua esperienza.",
        section3Title: "I tuoi diritti",
        section3Content: "Puoi richiedere la cancellazione dei tuoi dati in qualsiasi momento contattandoci."
      },
      terms: {
        title: "Termini e Condizioni",
        heading: "Termini di Servizio",
        lastUpdated: "Ultimo aggiornamento: Dicembre 2024",
        section1Title: "Accettazione",
        section1Content: "Utilizzando questa app accetti i presenti termini di servizio.",
        section2Title: "Uso del servizio",
        section2Content: "L'app è destinata ad uso personale per migliorare le relazioni di coppia attraverso suggerimenti pratici.",
        section3Title: "Limitazioni",
        section3Content: "I suggerimenti forniti sono a scopo informativo e non sostituiscono consulenze professionali."
      },
      checkin: {
        title: "Check-in Serale",
        didYouComplete: "Hai completato il suggerimento di oggi?",
        yes: "Sì, fatto!",
        no: "Non oggi",
        howDoYouFeel: "Come ti senti stasera?",
        moodSubtitle: "Il tuo umore generale",
        moodVeryLow: "Molto giù",
        moodLow: "Un po' giù",
        moodNeutral: "Così così",
        moodGood: "Bene",
        moodGreat: "Alla grande!",
        howIsRelationship: "Come sta andando la relazione?",
        relationshipSubtitle: "La tua percezione oggi",
        relVeryLow: "Molto difficile",
        relLow: "Un po' tesa",
        relNeutral: "Normale",
        relGood: "Bene",
        relGreat: "Molto bene!",
        reflections: "Riflessioni della sera",
        optionalFields: "Questi campi sono opzionali",
        reflection: "Com'è andata oggi?",
        reflectionPlaceholder: "Cosa è successo di significativo oggi nella vostra relazione?",
        gratitude: "Gratitudine",
        gratitudePlaceholder: "Per cosa sei grato/a oggi riguardo alla tua relazione?",
        intention: "Intenzione per domani",
        intentionPlaceholder: "Cosa vorresti fare domani per la relazione?",
        back: "Indietro",
        next: "Avanti",
        complete: "Completa",
        saved: "Check-in salvato! Buona notte 🌙",
        error: "Errore nel salvataggio",
        eveningReminder: "Check-in serale",
        reminderText: "Prenditi un momento per riflettere sulla giornata",
        alreadyDone: "Completato!"
      },
      notifications: {
        title: "Notifiche",
        new: "nuove",
        empty: "Nessuna notifica",
        dailyReminder: "Promemoria giornaliero",
        dailyReminderMsg: "Hai un suggerimento da completare oggi!",
        eveningCheckin: "Check-in serale",
        eveningCheckinMsg: "È il momento di riflettere sulla giornata",
        eventToday: "Evento oggi! 🎉",
        eventTomorrow: "Evento domani ⏰",
        eventWeek: "Evento tra 7 giorni 📅",
        event15Days: "Evento tra 15 giorni 🗓️",
        pushNudgeTitle: "Non perderti i promemoria",
        pushNudgeMsg: "Attiva le notifiche per ricevere i tuoi gesti al momento giusto.",
        pushModalTitle: "Resta in pista",
        pushModalBody: "I piccoli gesti si dimenticano facilmente. Un promemoria gentile al momento giusto ti aiuta a esserci — anche nelle giornate più piene.",
        pushModalSubtext: "Ti scriviamo solo quando conta. Zero spam, promesso.",
        pushModalCta: "Attiva le notifiche",
        pushModalDismiss: "Non ora",
        phrases: {
          neutral: [
            "Il gesto di oggi è pronto.",
            "Un momento per essere presente.",
            "Oggi conta questo.",
            "Un piccolo gesto. Fatto bene.",
            "È il tuo momento.",
            "Prenditi questo spazio.",
            "Meglio poco. Ma vero.",
            "Presenza, oggi.",
            "C’è una cosa che conta.",
            "Basta un gesto."
          ],
          morning: [
            "Inizia da qui.",
            "Prima di tutto: presenza.",
            "Oggi puoi fare questo.",
            "Un passo. Poi il resto.",
            "Non serve molto.",
            "Metti questo al centro."
          ],
          evening: [
            "C’è ancora tempo.",
            "Un gesto, prima di chiudere.",
            "Chiudi la giornata così.",
            "Meglio ora che mai.",
            "Un ultimo spazio per esserci."
          ],
          identity: [
            "Essere marito è pratica.",
            "Costruisci presenza.",
            "La costanza crea fiducia.",
            "Silenziosamente.",
            "Lo fai anche oggi.",
            "Non per fare. Per esserci."
          ],
          premium: [
            "Pensato per oggi.",
            "Adatto al tuo momento.",
            "Semplice. Alla tua portata.",
            "Oggi serve questo.",
            "Tiene conto di te.",
            "Allineato alla tua giornata."
          ],
          minimal: [
            "Presente.",
            "Oggi.",
            "È qui.",
            "Questo.",
            "Adesso.",
            "Conta."
          ]
        }
      },
      premium: {
        title: "Passa a Premium",
        subtitle: "Sblocca tutte le funzionalità per la tua relazione",
        heroTitle: "Diventa più preciso.",
        heroSubtitle: "Il gesto giusto, quando serve davvero.",
        perMonth: "mese",
        perYear: "anno",
        monthlyLabel: "Mensile",
        yearlyLabel: "Annuale",
        saveLabel: "-50%",
        pricingNote: "Meno di un caffè a settimana.",
        featureNotes: "Note illimitate sul partner",
        featureEvents: "Eventi speciali e promemoria",
        featureReminders: "Promemoria intelligenti",
        featureAI: "Suggerimenti AI prioritari",
        freeLabel: "Gratuito",
        premiumLabel: "Premium",
        suggestionPerDay: "suggerimento al giorno",
        basicNotes: "Note base",
        unlimitedSuggestions: "Suggerimenti illimitati",
        advancedNotes: "Note avanzate e promemoria",
        // Free tier bullets
        freeActionPerDay: "1 gesto al giorno",
        freeNotes: "Note sul partner",
        freeEventsReminders: "Promemoria eventi speciali",
        // Premium tier bullets
        premiumContext: "Contesto profondo & apprendimento progressivo",
        premiumAlternative: "Gesto alternativo",
        premiumCheckin: "Check-in serale",
        adaptiveTitle: "Precisione adattiva",
        adaptiveCopy: "Il motore apprende il tuo contesto — energia, abitudini, figli — e personalizza ogni gesto nel tempo.",
        activatePrecision: "Sblocca a vita",
        ctaMicrocopy: "Pagamento unico. Nessun abbonamento, nessun rinnovo.",
        // ── Accesso a vita (pagamento unico) ──
        lifetimeLabel: "A vita",
        lifetimeTagline: "Paghi una volta. È tuo per sempre.",
        lifetimeOneTime: "Pagamento unico",
        lifetimeOwned: "Hai l'accesso a vita",
        lifetimeOwnedDesc: "Niente da rinnovare, niente da annullare.",
        // Microcopy subordinata sull'addebito — sempre più piccola dell'importo
        // addebitato (App Store Guideline 3.1.2(c)).
        billingTerms: "Pagamento unico di {{price}}. Nessun abbonamento, nessun rinnovo automatico.",
        includesTitle: "Include",
        upgradeNow: "Attiva Premium",
        maybeLater: "Forse più tardi",
        securePayment: "Pagamento sicuro con Stripe",
        errorCheckout: "Errore nella creazione del pagamento",
        checking: "Verifica pagamento...",
        pleaseWait: "Attendi mentre verifichiamo il pagamento",
        confirmingPayment: "Conferma pagamento...",
        confirmingPaymentHint: "Stiamo confermando il tuo abbonamento. Resti qui ancora un attimo.",
        activating: "Stiamo attivando il tuo profilo...",
        activatingShort: "Attivazione...",
        activatingHint: "Un attimo, stiamo confermando l'attivazione del tuo abbonamento.",
        activationTimeout: "Stiamo ancora confermando il tuo abbonamento. Tocca Aggiorna per riprovare.",
        refresh: "Aggiorna",
        paymentConfirmed: "Pagamento confermato!",
        subscriptionActive: "Il tuo abbonamento e attivo.",
        stillConfirming: "Conferma ancora in corso",
        stillConfirmingHint: "Il pagamento e stato ricevuto. Tocca Aggiorna tra qualche secondo per completare l'attivazione.",
        successTitle: "Benvenuto in Premium!",
        nowPremium: "Sei ora Premium",
        successMessage: "Tutte le funzionalità premium sono sbloccate",
        goToDashboard: "Vai alla Dashboard",
        downloadApp: "Scarica l'app",
        errorTitle: "Errore Pagamento",
        errorMessage: "Si è verificato un problema nella verifica del pagamento. Riprova.",
        backToDashboard: "Torna alla Dashboard",
        backToHome: "Torna alla home",
        cancelTitle: "Pagamento Annullato",
        cancelMessage: "Nessun problema, puoi abbonarti quando vuoi.",
        tryAgain: "Riprova"
      },
      downloadApp: {
        title: "Scarica Better Husband",
        subtitle: "Il tuo abbonamento è attivo. Scarica l'app per iniziare.",
        backToHome: "Torna alla home"
      },
      guide: {
        menuLabel: "Guida",
        title: "Come funziona",
        subtitle: "GUIDA COMPLETA",
        heroTitle: "Tutto quello che devi sapere su BetterHusband",
        heroDescription: "Una guida completa per capire come l'app ti aiuta a essere più presente nella tua relazione, un piccolo gesto alla volta.",
        whatIs: {
          title: "Cos'è BetterHusband?",
          p1: "BetterHusband è un'app che ti aiuta a essere un marito e padre più presente e attento. Ogni giorno ricevi un suggerimento personalizzato — una micro-azione concreta calibrata sulla tua vita, la tua relazione e il tempo che hai a disposizione.",
          p2: "L'app non giudica e non ti dice che stai sbagliando. Ti allena, un piccolo gesto alla volta, a trasformare le buone intenzioni in abitudini reali.",
          tip: "La costanza conta più della perfezione. Anche un gesto piccolo, fatto con presenza, fa la differenza."
        },
        gettingStarted: {
          title: "Come iniziare",
          p1: "Dopo aver creato il tuo account (con Google o email), completerai un breve onboarding in 3 passi:",
          step1Title: "Situazione famigliare",
          step1Desc: "Hai figli? Che età hanno? Solo voi due?",
          step2Title: "Il tuo focus",
          step2Desc: "Su chi vuoi concentrarti: partner, figli, o entrambi?",
          step3Title: "Tempo disponibile",
          step3Desc: "Quanto tempo puoi dedicare ogni giorno? 5, 10 o 20 minuti.",
          tip: "Puoi modificare queste preferenze in qualsiasi momento dal tuo profilo."
        },
        dashboard: {
          title: "Dashboard — Il tuo gesto del giorno",
          p1: "Ogni giorno, la Dashboard ti mostra una singola azione personalizzata — il tuo gesto del giorno. Include una descrizione, il tempo stimato e la difficoltà. Hai tre opzioni:",
          doneLabel: "Fatto",
          doneDesc: "Segna il gesto come completato. Festeggia con i coriandoli! 🎉",
          skipLabel: "Passa",
          skipDesc: "Non è giornata? Nessun problema. Puoi passare senza giudizio.",
          altLabel: "Alternativa",
          altDesc: "Richiedi un suggerimento diverso se quello del giorno non ti convince.",
          p2: "La card mostra anche tag di contesto (tempo stimato, categoria, per chi è) per aiutarti a decidere in un secondo.",
          tip: "Tocca la card per vedere i dettagli completi del suggerimento, inclusi consigli su come realizzarlo."
        },
        forest: {
          title: "La Foresta della Presenza",
          p1: "Ogni gesto completato pianta un seme nella tua foresta virtuale. Più sei costante, più la foresta cresce e fiorisce — è una rappresentazione visiva del tuo impegno.",
          p2: "I livelli della foresta:",
          level1: "Seme — Hai appena iniziato",
          level2: "Germoglio — Stai crescendo",
          level3: "Albero — Sei affidabile",
          level4: "Foresta — Sei costante e presente"
        },
        checkin: {
          title: "Check-In Serale",
          p1: "Ogni sera, puoi fare un breve check-in guidato in 4 passi per riflettere sulla giornata:",
          step1: "Hai completato il suggerimento di oggi?",
          step2: "Come ti senti? (scala di umore con emoji)",
          step3: "Come va la relazione? (scala relazione con emoji)",
          step4: "Riflessione libera, gratitudine e intenzione per domani",
          tip: "Il check-in serale è una funzionalità Premium. Richiede solo 2 minuti e ti aiuta a restare consapevole."
        },
        stats: {
          title: "Statistiche",
          p1: "La pagina Statistiche mostra la tua costanza con una griglia di punti. Ogni punto rappresenta un giorno:",
          dotDone: "Fatto — gesto completato",
          dotSkipped: "Saltato — hai passato quel giorno",
          dotEmpty: "Vuoto — nessuna attività",
          p2: "Puoi visualizzare gli ultimi 7, 30 o 90 giorni. Il tuo livello di presenza cambia in base alla costanza:",
          levels: "Inizio → Crescita → Affidabile → Costante"
        },
        notes: {
          title: "Note",
          p1: "Le Note sono il tuo diario privato. Puoi scrivere riflessioni, pensieri sulla relazione, momenti che vuoi ricordare.",
          p2: "Ogni nota ha un titolo e un contenuto libero. Puoi crearle, modificarle e cancellarle quando vuoi.",
          tip: "Scrivi anche solo una frase. Alcuni momenti meritano di essere ricordati."
        },
        events: {
          title: "Eventi",
          p1: "Traccia le date importanti della tua relazione e famiglia. Tipi supportati:",
          type1: "Anniversari",
          type2: "Compleanni",
          type3: "Vacanze",
          type4: "Eventi personalizzati",
          p2: "Gli eventi possono essere ricorrenti (annuali) e l'app li usa per personalizzare i suggerimenti nei giorni importanti.",
          tip: "Aggiungi almeno l'anniversario e il compleanno della tua partner. L'app ti preparerà gesti speciali per quei giorni."
        },
        profile: {
          title: "Profilo",
          p1: "Il tuo profilo contiene le informazioni che l'app usa per personalizzare i suggerimenti:",
          item1: "Nome, email e foto profilo",
          item2: "Tipo di relazione (conviventi o sposati)",
          item3: "Situazione famigliare e figli",
          item4: "Focus e tempo disponibile"
        },
        settings: {
          title: "Impostazioni",
          p1: "Nelle impostazioni puoi configurare:",
          item1: "Lingua dell'app (Italiano / Inglese)",
          item2: "Notifiche push giornaliere",
          item3: "Promemoria eventi",
          item4: "Eliminazione account"
        },
        subscription: {
          title: "Abbonamento",
          p1: "BetterHusband offre un piano gratuito e uno Premium:",
          free1: "1 suggerimento al giorno",
          free2: "Note e diario",
          free3: "Gestione eventi",
          prem1: "Suggerimenti illimitati",
          prem2: "Alternativa AI",
          prem3: "Check-in serale",
          prem4: "Funzionalità avanzate",
          p2: "L'abbonamento costa €9,99/mese o €59,99/anno (7 giorni di prova gratuita). Puoi gestirlo dalla pagina Abbonamento."
        },
        support: {
          title: "Supporto",
          p1: "Hai bisogno di aiuto o hai un'idea per migliorare l'app? Dalla pagina Supporto puoi:",
          item1: "Inviare una richiesta di assistenza tecnica",
          item2: "Proporre una nuova funzionalità",
          p2: "Riceverai una risposta via email. Puoi anche allegare immagini per spiegare meglio il problema."
        },
        faq: {
          title: "Domande Frequenti",
          q1: "Quanto tempo ci vuole al giorno?",
          a1: "Da 5 a 20 minuti, in base a quello che hai scelto durante l'onboarding. E puoi sempre cambiare la preferenza.",
          q2: "L'app mi giudica se salto un giorno?",
          a2: "Mai. Non ci sono notifiche passive-aggressive. Se passi, va bene. Domani è un nuovo giorno.",
          q3: "Come vengono generati i suggerimenti?",
          a3: "Usiamo un sistema AI che combina il tuo profilo, i tuoi eventi, le tue preferenze e la cronologia per creare gesti personalizzati e mai ripetitivi.",
          q4: "I miei dati sono al sicuro?",
          a4: "Assolutamente. I tuoi dati sono criptati e non vengono condivisi con terze parti. Puoi eliminare il tuo account in qualsiasi momento dalle Impostazioni.",
          q5: "Posso usare l'app anche se non ho figli?",
          a5: "Certo! L'app si adatta alla tua situazione. Se non hai figli, i suggerimenti si concentreranno sulla tua relazione di coppia."
        }
      },
      support: {
        title: "Supporto",
        historyTitle: "I tuoi Tickets",
        notAuthenticated: "Devi essere autenticato per vedere lo storico delle richieste.",
        empty: "Non hai ancora inviato richieste di supporto.",
        emptyDesc: "Le tue richieste appariranno qui dopo averle inviate.",
        typeLabel: "Tipo",
        created: "Creato il",
        status: {
          open: "Aperta",
          inProgress: "In Lavorazione",
          closed: "Chiusa"
        },
        selectType: "Che tipo di richiesta vuoi fare?",
        type: {
          support: "Supporto",
          supportTitle: "Supporto Tecnico",
          supportDesc: "Hai un problema o bisogno di aiuto con l'app",
          feature: "Feature",
          featureTitle: "Suggerisci una Feature",
          featureDesc: "Hai un'idea per migliorare l'app"
        },
        form: {
          supportTitle: "Richiesta Supporto",
          featureTitle: "Suggerisci Feature",
          notAuthenticated: "Devi essere autenticato per inviare richieste di supporto.",
          subject: "Oggetto",
          subjectSupportPlaceholder: "Descrivi il problema...",
          subjectFeaturePlaceholder: "Descrivi la feature...",
          description: "Descrizione",
          descriptionSupportPlaceholder: "Spiega il problema o la richiesta di supporto...",
          descriptionFeaturePlaceholder: "Spiega la nuova feature o idea...",
          email: "Email per risposta",
          emailPlaceholder: "La tua email",
          images: "Immagini (opzionale)",
          dragImages: "Trascina qui le immagini o",
          selectImages: "clicca per selezionare",
          preview: "Anteprima immagini caricate:",
          uploading: "Caricamento immagini in corso...",
          uploadError: "Errore nel caricamento delle immagini. Riprova.",
          sending: "Invio...",
          send: "Invia richiesta",
          success: "Richiesta inviata con successo! Ti risponderemo via email.",
          error: "Errore nella creazione del ticket. Riprova più tardi."
        },
        detail: {
          title: "Dettagli Richiesta",
          description: "Descrizione",
          images: "Immagini Allegati",
          ticketId: "ID Richiesta",
          created: "Data Creazione",
          updated: "Ultimo Aggiornamento",
          email: "Email per Risposta"
        }
      }
    }
  },
  en: {
    common:{
      saving: "Saving...",
      save: "Save",
      cancel: "Cancel"
    },
    translation: {
      language: "en",
      meta: {
        title: "Better Husband — Daily suggestions to improve your relationship",
        description: "Daily, personalized micro-actions to strengthen your relationship."
      },
      splash: {
        title: "Small daily actions.\nBig differences in your relationship.",
        start: "Start"
      },
      welcome: {
        problem: "It's not that you don't want to do enough.\nIt's that no one ever told you how.",
        solution: "One suggestion per day, personalized for your relationship.",
        tone: "No judgments. No scores. Just practical help.",
        continue: "Continue"
      },
      onboarding: {
        title: "Let's get to know each other",
        subtitle: "There are no right or wrong answers",
        step1: {
          title: "Let's start",
          name: "What's your name?",
          namePlaceholder: "Your name",
          birthDate: "When were you born?",
          timezone: "What timezone are you in?",
          language: "Preferred language",
          italian: "Italiano",
          english: "English"
        },
        step2: {
          title: "Your relationship",
          type: "Current status",
          cohabitating: "Living together",
          married: "Married",
          duration: "How long have you been together?",
          durationLessThan1: "Less than 1 year",
          duration1to2: "1-2 years",
          duration2to5: "2-5 years",
          duration5to10: "5-10 years",
          duration10plus: "More than 10 years",
          children: "Do you have children?",
          yes: "Yes",
          no: "No",
          addChild: "Add child",
          removeChild: "Remove",
          childAge: "Child age",
          ageLessThan1: "Less than 1 year",
          age1: "1 year",
          age2: "2 years",
          age3: "3 years",
          age4: "4 years",
          age5: "5 years",
          age6: "6 years",
          age7: "7 years",
          age8: "8 years",
          age9: "9 years",
          age10: "10 years",
          age11: "11 years",
          age12: "12 years",
          age13: "13 years",
          age14: "14 years",
          age15: "15 years",
          age16: "16 years",
          age17: "17 years",
          age18plus: "18+ years"
        },
        step3: {
          title: "Daily life",
          communication: "How do you usually communicate?",
          commLow: "We talk little",
          commMedium: "We talk enough",
          commHigh: "We talk a lot",
          stress: "Perceived stress level",
          stressLow: "Low",
          stressMedium: "Medium",
          stressHigh: "High",
          intimacy: "Intimacy level",
          intimacyLow: "Low",
          intimacyMedium: "Medium",
          intimacyHigh: "High"
        },
        step4routine: {
          title: "Your Routine",
          subtitle: "Let's talk about your daily life",
          workSchedule: "Your work schedule",
          selectOption: "Select...",
          fullTime: "Full-time (office)",
          partTime: "Part-time",
          freelance: "Freelancer",
          remote: "Remote work",
          shiftWork: "Shift work",
          notWorking: "Not working",
          timeTogether: "How much time do you spend together each day?",
          timePoco: "Little",
          timeMedio: "Medium",
          timeMolto: "A lot",
          householdManagement: "Who manages home/children more often?",
          householdMainlyMe: "Mainly me",
          householdMainlyPartner: "Mainly my partner",
          householdBalanced: "Balanced"
        },
        step5tension: {
          title: "What Creates Tension",
          subtitle: "Select the main sources of tension (max 2)",
          communication: "Communication",
          time_together: "Time together",
          home_organization: "Home / Organization",
          children: "Children",
          intimacy: "Intimacy",
          work_stress: "Work / Stress",
          helpText: "This only helps us understand how to help you better"
        },
        step6partner: {
          title: "Your Partner's Name",
          subtitle: "What's the name of the special person in your life?",
          partnerName: "Partner's name",
          partnerNamePlaceholder: "e.g. Maria, John, Sofia..."
        },
        step6terms: {
          title: "Final Step",
          subtitle: "Before we start, please read and accept our policies",
          acceptTerms: "I have read and accept the",
          termsLink: "Terms of Service",
          acceptPrivacy: "I have read and accept the",
          privacyLink: "Privacy Policy",
          disclaimer: "You can withdraw your consent at any time from settings"
        },
        step6: {
          title: "Preferences",
          tone: "What tone do you prefer for suggestions?",
          gentle: "Gentle and encouraging",
          direct: "Direct and practical",
          ironic: "Ironic and light",
          reminderTime: "What time do you prefer the reminder?",
          availability: "How many minutes per day do you have for these suggestions?",
          minutes: "minutes"
        },
        step4: {
          title: "Preferences",
          tone: "What tone do you prefer for suggestions?",
          gentle: "Gentle and encouraging",
          direct: "Direct and practical",
          ironic: "Ironic and light",
          reminderTime: "What time do you prefer the reminder?",
          availability: "How many minutes per day do you have for these suggestions?",
          minutes: "minutes"
        },
        next: "Next",
        back: "Back",
        finish: "Complete"
      },
      onboardingV2: {
        title: "Welcome",
        subtitle: "Let's get started.",
        step0: {
          title: "Today.",
          body: "One small gesture. That's enough."
        },
        step1: {
          title: "Who's at home?",
          micro: "You can change this anytime.",
          none: "Just us",
          young: "Young kids",
          older: "Older kids",
          mixed: "A mix"
        },
        step2: {
          title: "What should this focus on?",
          micro: "Not a commitment. Just a direction.",
          partner: "My partner",
          kids: "My kids",
          both: "Both"
        },
        step3: {
          title: "How much time do you realistically have most days?",
          micro: "Keep it honest.",
          five: "5 minutes",
          ten: "10 minutes",
          twenty: "20 minutes"
        },
        step4: {
          title: "Do you want a reminder?",
          yes: "Yes, when it helps",
          no: "No, I'll open the app",
          micro: "No pressure either way."
        },
        step5: {
          title: "Want to add her name?",
          placeholder: "First name (optional)",
          micro: "This stays private."
        },
        stepTension: {
          title: "What creates the most tension?",
          micro: "Pick up to 2. Just helps us understand.",
          communication: "Communication",
          time_together: "Time together",
          home_organization: "Home / Organization",
          children: "Children",
          intimacy: "Intimacy",
          work_stress: "Work / Stress"
        },
        finish: "Start",
        next: "Continue",
        transition: "We're configuring your experience…"
      },
      dashboard: {
        title: "Today",
        loading: "Generating suggestion...",
        mainSuggestion: "Today's suggestion",
        microAction: "Quick micro-action",
        estimatedTime: "Estimated time",
        minutes: "min",
        done: "Done",
        postponed: "Later",
        notDone: "Skip",
        later: "Later",
        skip: "Next",
        next: "Next",
        feedbackSuccess: "Thanks for the feedback!",
        newSuggestion: "New suggestion generated!",
        addNotes: "Add notes (optional)",
        notesTitle: "Notes",
        notesSubtitle: "Add notes about how it went",
        notesPlaceholder: "e.g. She mentioned she'd like to go to the theater, she'd like to read a book...",
        complete: "Complete",
        cancel: "Cancel",
        testModeTitle: "Test Mode: Generate new suggestion",
        noSuggestionYet: "Suggestion not yet available",
        suggestionComesAt: "A new suggestion will be generated at your reminder time.",
        generateManually: "Generate manually",
        premiumLocked: {
          noSuggestions: "No suggestions",
          cta: "Subscribe to unlock your plan"
        },
        congratulations: [
          "That's it for today! You're amazing! 🌟",
          "Fantastic! Keep it up! 💪",
          "Great job! You're on the right track! 🎯",
          "Excellent! Every small gesture counts! ❤️",
          "Perfect! She'll definitely appreciate it! 💝",
          "Awesome! These gestures grow love! 🌱",
          "Congratulations! You're building something beautiful! 🏆",
          "Nice work! Consistency is key! 🔑",
          "Outstanding! Every day you're a better husband! 🌈"
        ],
        category: {
          husband: "Husband",
          father: "Father",
          partner: "Partner"
        },
        difficulty: {
          low: "Easy",
          medium: "Medium",
          high: "Challenging"
        },
        context: {
          anytime: "Anytime",
          morning: "Morning",
          afternoon: "Afternoon",
          evening: "Evening"
        },
        alternative: {
          title: "Alternative",
          question: "Need something different?",
          description: "I can suggest another personalized idea for today.",
          noThanks: "No, thanks",
          seeAlternative: "See an alternative",
          generating1: "Alright. Looking for your next action...",
          generating2: "Analyzing your preferences...",
          generating3: "Generating a personalized alternative...",
          generating4: "Almost ready!",
          success: "Here's another idea!",
          error: "Error loading alternative"
        },
        alternativePaywall: {
          title: "Not all days are the same.",
          description: "With Premium you can adapt the gesture to the moment you're in.",
          cta: "Activate Precision",
          continue: "Continue like this"
        },
        skip: {
          notToday: "Not today",
          dontFeelLikeIt: "I don't feel like it",
          revert: "Do it anyway",
          banner5: "You've been skipping for 5 days. Today's a good chance.",
          banner10: "It doesn't take much. Just one gesture."
        },
        precision: {
          title: "Need something more suitable?",
          description: "With Premium, the gesture adapts to your day.",
          cta: "Activate Precision",
          close: "Close"
        },
        premiumPrecision: {
          title: "Better, not more.",
          description: "Premium adapts the suggestion to energy, stress and routine.",
          cta: "Activate Precision",
          close: "Not now"
        },
        completion: {
          phrases: [
            "Well done! You made a difference today 💚",
            "Amazing! One gesture that matters 🌱",
            "You did it! You're growing 🌳",
            "Perfect. She'll notice ✨",
            "Every gesture brings you closer 💪",
            "Beautiful. Keep it up! 🔥",
            "Done! Your garden is growing 🌿",
            "You showed up. That's everything. 💚"
          ],
          addNote: "Add note",
          noteTitle: "Note",
          noteSubtitle: "One line, if you want.",
          notePlaceholder: "Write a note...",
          noteClose: "Close",
          noteSave: "Save",
          error: "Error. Try again.",
          slowConnection: "Slow connection"
        },
        forest: {
          label: "Your forest"
        }
      },
      profile: {
        title: "You",
        settings: "Settings",
        history: "History",
        language: "Language",
        reminderTime: "Reminder time",
        tone: "Suggestions tone",
        save: "Save",
        saved: "Profile updated!",
        personalInfo: "Personal info",
        name: "Name",
        email: "Email",
        birthDate: "Birth date",
        timezone: "Timezone",
        yourSituation: "Your situation",
        homeMembers: "Who's at home",
        whatMatters: "What matters most",
        timeAvailable: "Time available",
        edit: "Edit",
        editYourData: "Edit your data",
        updateYourProfile: "Update your profile",
        back: "Back",
        kids_cluster: {
          none: "Just us",
          young: "Young kids",
          older: "Older kids",
          mixed: "A mix"
        },
        focus: {
          partner: "My partner",
          self: "Myself",
          family: "Family"
        },
        relationship: {
          cohabitating: "Living together",
          married: "Married"
        }
      },
      history: {
        title: "History",
        empty: "No suggestions yet",
        emptyEncouragement: "Every journey starts with a small step.",
        emptyIcon: "trending-up",
        completed: "Completed",
        total: "Total",
        status: {
          done: "Done",
          postponed: "Postponed",
          not_done: "Not done",
          pending: "Pending"
        }
      },
      auth: {
        login: "Login",
        register: "Sign Up",
        loginButton: "Login",
        registerButton: "Create Account",
        name: "Name",
        namePlaceholder: "Your name",
        emailPlaceholder: "your@email.com",
        tagline: "Small daily actions. Big differences in your relationship.",
        loginSuccess: "Logged in successfully!",
        registerSuccess: "Account created successfully!",
        loginError: "Invalid email or password",
        registerError: "Registration error",
        noAccount: "Don't have an account? Sign up above",
        hasAccount: "Already have an account? Login above"
      },
      nav: {
        home: "Home",
        profile: "You",
        history: "History",
        events: "Events",
        stats: "Presence",
        notes: "Notes"
      },
      events: {
        title: "Special Events",
        eventAdded: "Event added!",
        eventDeleted: "Event deleted",
        eventType: "Event type",
        anniversary: "Anniversary",
        birthday: "Birthday",
        vacation: "Vacation",
        custom: "Custom",
        eventTitle: "Title",
        titlePlaceholder: "e.g. Wedding Anniversary",
        eventDate: "Date",
        date: "Date",
        recurring: "Repeat",
        recurrence_none: "No recurrence",
        recurrence_daily: "Daily",
        recurrence_weekly: "Weekly",
        recurrence_monthly: "Monthly",
        recurrence_yearly: "Yearly",
        notes: "Notes",
        save: "Save",
        cancel: "Cancel",
        empty: "No events yet",
        emptyEncouragement: "Important moments don't always announce themselves.",
        emptyIcon: "calendar",
        today: "Today",
        back: "Back",
        eventUpdated: "Event updated"
      },
      stats: {
        title: "Presence",
        days: "days",
        present: "Present.",
        lastDays: "Last {{count}} days",
        daysOfPresence: "{{count}} / {{total}} days present",
        presenceDescription: "Counts only when you mark Done.",
        timeline: "Timeline",
        done: "Done",
        skipped: "Skipped",
        missed: "Missed",
        categories: "Where you showed up",
        timeInvested: "Time Invested",
        hoursTotal: "total hours",
        notes: "Notes",
        precision: "Precision",
        categoryBreakdown: "Category Breakdown",
        timeMetrics: "Time Metrics",
        avgMinutes: "Average minutes",
        weeklyAvg: "Weekly average",
        consistencyScore: "Consistency Score",
        activeAdaptation: "Active Adaptation",
        adaptationText: "Evenings work better for you.\nYou complete short gestures more.",
        timeSection: "Time",
        timeDescription: "~{{hours}}h in the last {{days}} days",
        overview: "OVERVIEW",
        starting: "Starting.",
        growing: "Growing.",
        reliable: "Reliable.",
        unwavering: "Unwavering.",
        dotCaption: "Every dot is a choice. Every choice builds presence.",
        gesturesMade: "{{count}} gestures made.",
        dotDone: "DONE",
        dotSkipped: "SKIPPED",
        dotEmpty: "—",
        dotNoDetail: "Gesture completed.",
        dotSkippedDetail: "You skipped this day.",
        dotEmptyDetail: "No gesture for this day.",
        streakLegendary: "Legendary",
        streakChampion: "Champion",
        streakOnFire: "On Fire",
        streakExcellent: "Excellent",
        streakStart: "Starting",
      },
      metrics: {
        title: "Metrics",
        week: "Week",
        month: "Month",
        all: "All",
        engagementScore: "Engagement Score",
        completionRate: "Completion Rate",
        currentStreak: "Current Streak",
        best: "Best",
        actionsDone: "Actions Done",
        assigned: "assigned",
        timeInvested: "Time Invested",
        checkinsAndMood: "Check-ins & Mood",
        totalCheckins: "Total Check-ins",
        avgMood: "Avg Mood",
        moodTrend: "Mood Trend",
        byCategory: "By Category",
        weeklyBreakdown: "Weekly Breakdown",
        skippedActions: "Skipped Actions",
        skippedDesc: "No worries, every day is a fresh start",
        noData: "No data yet",
      },
      categories: {
        presence: "Presence",
        repair: "Home",
        logistics: "Logistics",
        connection: "Connection",
        play: "Kids",
        change_of_scene: "Change of Scene",
        other: "Other",
        husband: "Husband",
        father: "Father"
      },
      menu: {
        title: "Menu",
        profile: "You",
        game: "Husband Simulator (game)",
        metrics: "Metrics",
        settings: "Settings",
        subscription: "Subscription",
        logout: "Logout",
        privacy: "Privacy",
        terms: "Terms and Conditions",
        user: "You"
      },
      notes: {
        title: "Notes",
        search: "Search notes...",
        newNote: "New note",
        newReflection: "New Reflection",
        journeyTitle: "The journey so far.",
        journeySubtitle: "Every note is a step toward more presence and clarity.",
        today: "TODAY",
        daysAgo: "{{count}} DAY AGO",
        daysAgo_plural: "{{count}} DAYS AGO",
        titlePlaceholder: "Title",
        contentPlaceholder: "Write your note here...",
        cancel: "Cancel",
        create: "Create",
        save: "Save",
        back: "Back",
        noContent: "No content",
        empty: "No notes yet. Create your first note!",
        emptyEncouragement: "Some moments are worth showing up for.",
        emptyIcon: "book-open",
        noResults: "No results found",
        created: "Note created!",
        updated: "Note updated!",
        deleted: "Note deleted!",
        error: "Error during operation",
        titleRequired: "Title is required",
        lastUpdated: "Last updated"
      },
      settings: {
        title: "Settings",
        language: "Language",
        notifications: "Notifications",
        notificationsDesc: "Receive daily reminders",
        reminderTime: "Reminder time",
        adminProgQuestions: "Progressive questions",
        adminProgQuestionsClicked: "Admin test triggered",
        saved: "Settings saved!",
        error: "Error saving settings",
        pushNotifications: "Push notifications",
        pushEnabledDesc: "You'll receive notifications even when the app is closed",
        pushDisabledDesc: "Enable to receive reminders",
        pushDeniedDesc: "Permission denied. Enable from browser settings",
        pushEnabled: "Push notifications enabled!",
        pushDisabled: "Push notifications disabled",
        pushDenied: "Notification permission denied",
        pushManagedNote: "Push notifications are managed by your device — use your phone settings to control them.",
        progressive: {
          modalTitle: "Quick question",
          partner_name: {
            title: "Want to add her name?",
            placeholder: "First name (optional)"
          },
          number_of_children: {
            title: "How many children do you have?",
            options: {
              "1": "1",
              "2": "2",
              "3": "3",
              "4+": "4+"
            }
          },
          schedule_type: {
            title: "How are your days lately?",
            options: {
              predictable: "Predictable",
              chaotic: "Chaotic",
              shifts: "Shifts"
            }
          },
          together_time: {
            title: "Lately, do you get time together?",
            options: {
              low: "Low",
              medium: "Medium",
              high: "High"
            }
          },
          home_load: {
            title: "At home, who carries most of the load?",
            options: {
              me: "Me",
              partner: "Partner",
              even: "Even",
              varies: "Varies"
            }
          },
          focus_tension: {
            title: "If you had to pick one area to handle better this month:",
            options: {
              communication: "Communication",
              time_together: "Time together",
              home_logistics: "Home / Logistics",
              work_stress: "Work / Stress",
              intimacy: "Intimacy",
              parenting: "Parenting"
            }
          }
        },
        dailyReminder: "Daily reminder",
        dailyReminderDesc: "Get notified with the suggestion of the day",
        eventReminders: "Event reminders",
        eventRemindersDesc: "Get notified for special events",
        checkinTime: "Evening check-in time",
        checkinTimeDesc: "When to receive the check-in reminder",
        preferences: "Preferences",
        tone: "Suggestion tone",
        toneGentle: "Gentle",
        toneDirect: "Direct",
        toneIronic: "Ironic",
        availability: "Available time",
        minutesDay: "min/day",
        partner: "Partner",
        partnerName: "Partner's name",
        partnerNamePlaceholder: "e.g. Maria, John...",
        account: "Account",
        editProfile: "Edit profile",
        subscription: "Subscription",
        freePlan: "Free plan",
        dangerZone: "Danger zone",
        deleteAccount: {
          title: "Delete account",
          description: "This action is permanent and irreversible",
          confirmMessage: "Are you sure? All your data will be permanently deleted.",
          confirm: "Delete",
          cancel: "Cancel",
          deleting: "Deleting...",
          success: "Account successfully deleted",
          error: "Error deleting account"
        },
        about: "About",
        privacy: "Privacy",
        terms: "Terms and conditions",
        version: "Version"
      },
      subscription: {
        title: "Subscription",
        back: "Back",
        returnedFromPortal: "Welcome back!",
        free: "Free",
        premium: "Premium",
        freePrice: "Free",
        noSubscription: "No subscription",
        noSubscriptionDesc: "Lifetime access is required to use the app's features",
        inactive: "Inactive",
        dismiss: "Close",
        dismissRequiredAccess: "Close subscription screen",
        subscribe: "Subscribe",
        startTrial: "Start 7-day free trial",
        recommended: "Recommended",
        current: "Current",
        currentPlan: "Current plan",
        upgrade: "Activate Precision",
        management: "Management",
        managePayment: "Manage payment",
        managePaymentDesc: "Update payment method",
        billingHistory: "Billing history",
        billingHistoryDesc: "View past invoices",
        cancelSubscription: "Cancel subscription",
        cancelDesc: "Cancel automatic renewal",
        infoTitle: "Information",
        infoPremium: "Your subscription renews automatically. You can cancel anytime.",
        infoFree: "Access to app features requires lifetime access. One payment, then it's yours for good.",
        managingRedirect: "Opening management portal...",
        errorPortal: "Error opening management portal",
        noCustomerId: "Subscription not manageable. Contact support.",
        manualPremiumTitle: "Premium Subscription Active",
        manualPremiumDesc: "Your account is Premium. Manage your subscription, change plan or cancel directly from the store you bought it on.",
        nextBilling: "Next billing",
        trialEndsOn: "Trial ends on",
        manageOnGoogle: "Manage on Google Play",
        manageOnGoogleDesc: "Opens Google Play › Subscriptions",
        manageOnStripe: "Manage subscription",
        manageOnStripeDesc: "Open the billing portal to update payment or cancel",
        manageOnApple: "Manage in Apple Subscriptions",
        manageOnAppleDesc: "Opens Settings › Apple ID › Subscriptions",
        storeManagedDescGoogle: "Your subscription is billed by Google Play. Change plan, update payment, or cancel from your Google account.",
        storeManagedDescApple: "Your subscription is billed by Apple. Change plan, update payment, or cancel from your Apple account.",
        storeManagedDescWeb: "If you subscribed on the web, manage your subscription below. If you subscribed in the mobile app, use Google Play or the App Store on your phone.",
        webNotAvailable: "Subscriptions are only available in the BetterHusband mobile app. Please install it from the App Store or Google Play.",
        feature1Free: "1 action per day",
        feature2Free: "Partner notes",
        feature3Free: "Special events reminders",
        feature1Premium: "Deep context & progressive learning",
        feature2Premium: "Action alternative",
        feature3Premium: "Evening check-in"
      },
      privacy: {
        title: "Privacy",
        heading: "Privacy Policy",
        lastUpdated: "Last updated: December 2024",
        section1Title: "Data Collection",
        section1Content: "We only collect data necessary to provide you with personalized suggestions. Your data is encrypted and protected.",
        section2Title: "Data Usage",
        section2Content: "Your data is used exclusively to generate personalized suggestions and improve your experience.",
        section3Title: "Your Rights",
        section3Content: "You can request deletion of your data at any time by contacting us."
      },
      terms: {
        title: "Terms and Conditions",
        heading: "Terms of Service",
        lastUpdated: "Last updated: December 2024",
        section1Title: "Acceptance",
        section1Content: "By using this app, you accept these terms of service.",
        section2Title: "Use of Service",
        section2Content: "The app is intended for personal use to improve couple relationships through practical suggestions.",
        section3Title: "Limitations",
        section3Content: "The suggestions provided are for informational purposes and do not replace professional advice."
      },
      checkin: {
        title: "Evening Check-in",
        didYouComplete: "Did you complete today's suggestion?",
        yes: "Yes, done!",
        no: "Not today",
        howDoYouFeel: "How are you feeling tonight?",
        moodSubtitle: "Your general mood",
        moodVeryLow: "Very down",
        moodLow: "A bit down",
        moodNeutral: "So-so",
        moodGood: "Good",
        moodGreat: "Great!",
        howIsRelationship: "How is the relationship going?",
        relationshipSubtitle: "Your perception today",
        relVeryLow: "Very difficult",
        relLow: "A bit tense",
        relNeutral: "Normal",
        relGood: "Good",
        relGreat: "Very good!",
        reflections: "Evening reflections",
        optionalFields: "These fields are optional",
        reflection: "How did today go?",
        reflectionPlaceholder: "What happened today that was significant in your relationship?",
        gratitude: "Gratitude",
        gratitudePlaceholder: "What are you grateful for today regarding your relationship?",
        intention: "Intention for tomorrow",
        intentionPlaceholder: "What would you like to do tomorrow for the relationship?",
        back: "Back",
        next: "Next",
        complete: "Complete",
        saved: "Check-in saved! Good night 🌙",
        error: "Error saving",
        eveningReminder: "Evening check-in",
        reminderText: "Take a moment to reflect on the day",
        alreadyDone: "Completed!"
      },
      notifications: {
        title: "Notifications",
        new: "new",
        empty: "No notifications",
        dailyReminder: "Daily reminder",
        dailyReminderMsg: "You have a suggestion to complete today!",
        eveningCheckin: "Evening check-in",
        eveningCheckinMsg: "It's time to reflect on the day",
        eventToday: "Event today! 🎉",
        eventTomorrow: "Event tomorrow ⏰",
        eventWeek: "Event in 7 days 📅",
        event15Days: "Event in 15 days 🗓️",
        pushNudgeTitle: "Don't miss your reminders",
        pushNudgeMsg: "Enable notifications to get your gestures at the right moment.",
        pushModalTitle: "Stay on track",
        pushModalBody: "Small gestures are easy to forget. A gentle nudge at the right moment helps you show up — even on busy days.",
        pushModalSubtext: "We only send what matters. No spam, ever.",
        pushModalCta: "Enable notifications",
        pushModalDismiss: "Not now",
        phrases: {
          neutral: [
            "The gesture for today is ready.",
            "A moment to be present.",
            "Today this matters.",
            "A small gesture. Done well.",
            "This is your moment.",
            "Take this space.",
            "Better small. But true.",
            "Presence, today.",
            "There's one thing that matters.",
            "Just one gesture."
          ],
          morning: [
            "Start here.",
            "First thing: presence.",
            "Today you can do this.",
            "One step. Then the rest.",
            "It doesn't take much.",
            "Put this at the center."
          ],
          evening: [
            "There's still time.",
            "One gesture before you close the day.",
            "Close the day like this.",
            "Better now than never.",
            "One last space to be present."
          ],
          identity: [
            "Being a husband is practice.",
            "Build presence.",
            "Consistency builds trust.",
            "Silently.",
            "You do it today too.",
            "Not to perform. To be present."
          ],
          premium: [
            "Designed for today.",
            "Fitted to your moment.",
            "Simple. Within reach.",
            "Today this is useful.",
            "Takes you into account.",
            "Aligned with your day."
          ],
          minimal: [
            "Present.",
            "Today.",
            "Here.",
            "This.",
            "Now.",
            "Counts."
          ]
        }
      },
      premium: {
        title: "Go Premium",
        subtitle: "Unlock all features for your relationship",
        heroTitle: "Get more precise.",
        heroSubtitle: "The right gesture, when it matters.",
        perMonth: "month",
        perYear: "year",
        monthlyLabel: "Monthly",
        yearlyLabel: "Annual",
        saveLabel: "-50%",
        pricingNote: "Less than a coffee a week.",
        featureNotes: "Unlimited partner notes",
        featureEvents: "Special events & reminders",
        featureReminders: "Smart event reminders",
        featureAI: "Priority AI suggestions",
        freeLabel: "Free",
        premiumLabel: "Premium",
        suggestionPerDay: "suggestion per day",
        basicNotes: "Basic notes",
        unlimitedSuggestions: "Unlimited suggestions",
        advancedNotes: "Advanced notes & reminders",
        // Free tier bullets
        freeActionPerDay: "1 action per day",
        freeNotes: "Partner notes",
        freeEventsReminders: "Special events reminders",
        // Premium tier bullets
        premiumContext: "Deep context & progressive learning",
        premiumAlternative: "Action alternative",
        premiumCheckin: "Evening check-in",
        adaptiveTitle: "Adaptive Precision",
        adaptiveCopy: "The engine learns your context — energy, habits, kids — and personalizes every gesture over time.",
        activatePrecision: "Unlock for life",
        ctaMicrocopy: "One payment. No subscription, no renewals.",
        // ── Lifetime access (one-time purchase) ──
        lifetimeLabel: "Lifetime",
        lifetimeTagline: "Pay once. Yours forever.",
        lifetimeOneTime: "One-time payment",
        lifetimeOwned: "You have lifetime access",
        lifetimeOwnedDesc: "Nothing to renew, nothing to cancel.",
        // Subordinate billing microcopy — always rendered smaller than the
        // billed amount itself (App Store Guideline 3.1.2(c)).
        billingTerms: "One-time payment of {{price}}. No subscription, no auto-renewal.",
        includesTitle: "Includes",
        upgradeNow: "Activate Premium",
        maybeLater: "Maybe later",
        securePayment: "Secure payment with Stripe",
        errorCheckout: "Error creating checkout session",
        checking: "Checking payment...",
        pleaseWait: "Please wait while we verify your payment",
        confirmingPayment: "Confirming payment...",
        confirmingPaymentHint: "We are confirming your subscription. Stay here for a moment.",
        activating: "Activating your profile...",
        activatingShort: "Activating...",
        activatingHint: "Hang tight while we confirm your subscription is active.",
        activationTimeout: "We're still confirming your subscription. Tap Refresh to retry.",
        refresh: "Refresh",
        paymentConfirmed: "Payment Confirmed!",
        subscriptionActive: "Your subscription is active.",
        stillConfirming: "Still confirming payment",
        stillConfirmingHint: "Payment was received. Tap Refresh in a few seconds to finish activation.",
        successTitle: "Welcome to Premium!",
        nowPremium: "You are now Premium",
        successMessage: "All premium features are now unlocked",
        goToDashboard: "Go to Dashboard",
        downloadApp: "Download the app",
        errorTitle: "Payment Error",
        errorMessage: "There was a problem verifying your payment. Please try again.",
        backToDashboard: "Back to Dashboard",
        backToHome: "Back to home",
        cancelTitle: "Payment Cancelled",
        cancelMessage: "No worries, you can upgrade anytime.",
        tryAgain: "Try Again"
      },
      downloadApp: {
        title: "Download Better Husband",
        subtitle: "Your subscription is active. Download the app to get started.",
        backToHome: "Back to home"
      },
      guide: {
        menuLabel: "Guide",
        title: "How it works",
        subtitle: "COMPLETE GUIDE",
        heroTitle: "Everything you need to know about BetterHusband",
        heroDescription: "A complete guide to understand how the app helps you be more present in your relationship, one small gesture at a time.",
        whatIs: {
          title: "What is BetterHusband?",
          p1: "BetterHusband is an app that helps you become a more present and attentive husband and father. Every day you receive a personalized suggestion — a concrete micro-action calibrated to your life, your relationship, and the time you have available.",
          p2: "The app doesn't judge and doesn't tell you you're doing it wrong. It coaches you, one small gesture at a time, to turn good intentions into real habits.",
          tip: "Consistency matters more than perfection. Even a small gesture, done with presence, makes a difference."
        },
        gettingStarted: {
          title: "Getting Started",
          p1: "After creating your account (with Google or email), you'll complete a short 3-step onboarding:",
          step1Title: "Family situation",
          step1Desc: "Do you have kids? How old are they? Just the two of you?",
          step2Title: "Your focus",
          step2Desc: "Who do you want to focus on: partner, kids, or both?",
          step3Title: "Available time",
          step3Desc: "How much time can you dedicate each day? 5, 10, or 20 minutes.",
          tip: "You can change these preferences at any time from your profile."
        },
        dashboard: {
          title: "Dashboard — Your Daily Gesture",
          p1: "Every day, the Dashboard shows you a single personalized action — your gesture of the day. It includes a description, estimated time, and difficulty. You have three options:",
          doneLabel: "Done",
          doneDesc: "Mark the gesture as completed. Celebrate with confetti! 🎉",
          skipLabel: "Skip",
          skipDesc: "Not your day? No problem. You can skip without judgment.",
          altLabel: "Alternative",
          altDesc: "Request a different suggestion if today's doesn't feel right.",
          p2: "The card also shows context tags (estimated time, category, who it's for) to help you decide in a second.",
          tip: "Tap the card to see full suggestion details, including tips on how to carry it out."
        },
        forest: {
          title: "The Presence Forest",
          p1: "Every completed gesture plants a seed in your virtual forest. The more consistent you are, the more your forest grows and flourishes — it's a visual representation of your commitment.",
          p2: "Forest levels:",
          level1: "Seed — You've just started",
          level2: "Sprout — You're growing",
          level3: "Tree — You're reliable",
          level4: "Forest — You're consistent and present"
        },
        checkin: {
          title: "Evening Check-In",
          p1: "Every evening, you can do a brief guided check-in in 4 steps to reflect on your day:",
          step1: "Did you complete today's suggestion?",
          step2: "How are you feeling? (mood scale with emoji)",
          step3: "How's the relationship? (relationship scale with emoji)",
          step4: "Free reflection, gratitude, and intention for tomorrow",
          tip: "The evening check-in is a Premium feature. It takes only 2 minutes and helps you stay mindful."
        },
        stats: {
          title: "Statistics",
          p1: "The Stats page shows your consistency with a dot grid. Each dot represents a day:",
          dotDone: "Done — gesture completed",
          dotSkipped: "Skipped — you passed that day",
          dotEmpty: "Empty — no activity",
          p2: "You can view the last 7, 30, or 90 days. Your presence level changes based on consistency:",
          levels: "Starting → Growing → Reliable → Unwavering"
        },
        notes: {
          title: "Notes",
          p1: "Notes are your private journal. You can write reflections, thoughts about your relationship, moments you want to remember.",
          p2: "Each note has a title and free-form content. You can create, edit, and delete them anytime.",
          tip: "Write even just one sentence. Some moments are worth remembering."
        },
        events: {
          title: "Events",
          p1: "Track important dates for your relationship and family. Supported types:",
          type1: "Anniversaries",
          type2: "Birthdays",
          type3: "Vacations",
          type4: "Custom events",
          p2: "Events can be recurring (yearly) and the app uses them to personalize suggestions on important days.",
          tip: "Add at least your anniversary and your partner's birthday. The app will prepare special gestures for those days."
        },
        profile: {
          title: "Profile",
          p1: "Your profile contains the information the app uses to personalize suggestions:",
          item1: "Name, email, and profile photo",
          item2: "Relationship type (living together or married)",
          item3: "Family situation and kids",
          item4: "Focus and available time"
        },
        settings: {
          title: "Settings",
          p1: "In settings you can configure:",
          item1: "App language (Italian / English)",
          item2: "Daily push notifications",
          item3: "Event reminders",
          item4: "Account deletion"
        },
        subscription: {
          title: "Subscription",
          p1: "BetterHusband offers a Free and a Premium plan:",
          free1: "1 suggestion per day",
          free2: "Notes and journal",
          free3: "Event management",
          prem1: "Unlimited suggestions",
          prem2: "AI alternatives",
          prem3: "Evening check-in",
          prem4: "Advanced features",
          p2: "Subscription costs €9.99/month or €59.99/year (7-day free trial included). You can manage it from the Subscription page."
        },
        support: {
          title: "Support",
          p1: "Need help or have an idea to improve the app? From the Support page you can:",
          item1: "Submit a technical support request",
          item2: "Suggest a new feature",
          p2: "You'll receive a reply via email. You can also attach images to better explain the issue."
        },
        faq: {
          title: "Frequently Asked Questions",
          q1: "How much time does it take per day?",
          a1: "5 to 20 minutes, based on what you chose during onboarding. And you can always change your preference.",
          q2: "Does the app judge me if I skip a day?",
          a2: "Never. There are no passive-aggressive notifications. If you skip, that's fine. Tomorrow is a new day.",
          q3: "How are suggestions generated?",
          a3: "We use an AI system that combines your profile, events, preferences, and history to create personalized, never-repetitive gestures.",
          q4: "Is my data safe?",
          a4: "Absolutely. Your data is encrypted and never shared with third parties. You can delete your account at any time from Settings.",
          q5: "Can I use the app if I don't have kids?",
          a5: "Of course! The app adapts to your situation. If you don't have kids, suggestions will focus on your couple relationship."
        }
      },
      support: {
        title: "Support",
        historyTitle: "Your Tickets",
        notAuthenticated: "You must be authenticated to view request history.",
        empty: "You haven't submitted any support requests yet.",
        emptyDesc: "Your requests will appear here after you submit them.",
        typeLabel: "Type",
        created: "Created on",
        status: {
          open: "Open",
          inProgress: "In Progress",
          closed: "Closed"
        },
        selectType: "What type of request do you want to make?",
        type: {
          support: "Support",
          supportTitle: "Technical Support",
          supportDesc: "You have a problem or need help with the app",
          feature: "Feature",
          featureTitle: "Suggest a Feature",
          featureDesc: "You have an idea to improve the app"
        },
        form: {
          supportTitle: "Support Request",
          featureTitle: "Suggest Feature",
          notAuthenticated: "You must be authenticated to submit support requests.",
          subject: "Subject",
          subjectSupportPlaceholder: "Describe the problem...",
          subjectFeaturePlaceholder: "Describe the feature...",
          description: "Description",
          descriptionSupportPlaceholder: "Explain the problem or support request...",
          descriptionFeaturePlaceholder: "Explain the new feature or idea...",
          email: "Reply email",
          emailPlaceholder: "Your email",
          images: "Images (optional)",
          dragImages: "Drag images here or",
          selectImages: "click to select",
          preview: "Preview of uploaded images:",
          uploading: "Uploading images...",
          uploadError: "Error uploading images. Please try again.",
          sending: "Sending...",
          send: "Send request",
          success: "Request sent successfully! We'll reply via email.",
          error: "Error creating ticket. Please try again later."
        },
        detail: {
          title: "Request Details",
          description: "Description",
          images: "Attached Images",
          ticketId: "Request ID",
          created: "Creation Date",
          updated: "Last Update",
          email: "Reply Email"
        }
      }
    }
  },
  es: {
    common: {
      saving: "Guardando...",
      save: "Guardar",
      cancel: "Cancelar"
    },
    translation: {
      language: "es",
      meta: {
        title: "Better Husband — Sugerencias diarias para mejorar tu relación",
        description: "Micro-acciones diarias y personalizadas para fortalecer tu relación."
      },
      splash: {
        title: "Pequeñas acciones diarias.\nGrandes diferencias en tu relación.",
        start: "Empezar"
      },
      welcome: {
        problem: "No es que no quieras hacer suficiente.\nEs que nadie te enseñó cómo hacerlo.",
        solution: "Una sugerencia al día, personalizada para tu relación.",
        tone: "Sin juicios. Sin puntuaciones. Solo ayuda práctica.",
        continue: "Continuar"
      },
      onboarding: {
        title: "Conociéndonos",
        subtitle: "No hay respuestas correctas ni incorrectas",
        step1: {
          title: "Empecemos",
          name: "¿Cómo te llamas?",
          namePlaceholder: "Tu nombre",
          birthDate: "¿Cuándo naciste?",
          timezone: "¿En qué zona horaria estás?",
          language: "Idioma preferido",
          italian: "Italiano",
          english: "English"
        },
        step2: {
          title: "Tu relación",
          type: "Estado actual",
          cohabitating: "Conviviendo",
          married: "Casados",
          duration: "¿Cuánto tiempo llevan juntos?",
          durationLessThan1: "Menos de 1 año",
          duration1to2: "1-2 años",
          duration2to5: "2-5 años",
          duration5to10: "5-10 años",
          duration10plus: "Más de 10 años",
          children: "¿Tienen hijos?",
          yes: "Sí",
          no: "No",
          addChild: "Agregar hijo",
          removeChild: "Eliminar",
          childAge: "Edad del hijo",
          ageLessThan1: "Menos de 1 año",
          age1: "1 año",
          age2: "2 años",
          age3: "3 años",
          age4: "4 años",
          age5: "5 años",
          age6: "6 años",
          age7: "7 años",
          age8: "8 años",
          age9: "9 años",
          age10: "10 años",
          age11: "11 años",
          age12: "12 años",
          age13: "13 años",
          age14: "14 años",
          age15: "15 años",
          age16: "16 años",
          age17: "17 años",
          age18plus: "18+ años"
        },
        step3: {
          title: "Vida diaria",
          communication: "¿Cómo se comunican habitualmente?",
          commLow: "Hablamos poco",
          commMedium: "Hablamos suficiente",
          commHigh: "Hablamos mucho",
          stress: "Nivel de estrés percibido",
          stressLow: "Bajo",
          stressMedium: "Medio",
          stressHigh: "Alto",
          intimacy: "Nivel de intimidad",
          intimacyLow: "Bajo",
          intimacyMedium: "Medio",
          intimacyHigh: "Alto"
        },
        step4routine: {
          title: "Tu rutina",
          subtitle: "Hablemos de tu vida diaria",
          workSchedule: "Tu horario de trabajo",
          selectOption: "Seleccionar...",
          fullTime: "Tiempo completo (oficina)",
          partTime: "Medio tiempo",
          freelance: "Freelancer",
          remote: "Trabajo remoto",
          shiftWork: "Por turnos",
          notWorking: "Sin trabajo",
          timeTogether: "¿Cuánto tiempo pasan juntos cada día?",
          timePoco: "Poco",
          timeMedio: "Medio",
          timeMolto: "Mucho",
          householdManagement: "¿Quién gestiona más el hogar/los hijos?",
          householdMainlyMe: "Principalmente yo",
          householdMainlyPartner: "Principalmente mi pareja",
          householdBalanced: "Equilibrado"
        },
        step5tension: {
          title: "Qué genera tensión",
          subtitle: "Selecciona las principales fuentes de tensión (máx. 2)",
          communication: "Comunicación",
          time_together: "Tiempo juntos",
          home_organization: "Hogar / Organización",
          children: "Hijos",
          intimacy: "Intimidad",
          work_stress: "Trabajo / Estrés",
          helpText: "Esto solo nos ayuda a entender cómo ayudarte mejor"
        },
        step6partner: {
          title: "El nombre de tu pareja",
          subtitle: "¿Cómo se llama la persona especial en tu vida?",
          partnerName: "Nombre de la pareja",
          partnerNamePlaceholder: "p.ej. María, Juan, Sofía..."
        },
        step6terms: {
          title: "Último paso",
          subtitle: "Antes de empezar, lee y acepta nuestras políticas",
          acceptTerms: "He leído y acepto los",
          termsLink: "Términos de servicio",
          acceptPrivacy: "He leído y acepto la",
          privacyLink: "Política de privacidad",
          disclaimer: "Puedes retirar tu consentimiento en cualquier momento desde la configuración"
        },
        step6: {
          title: "Preferencias",
          tone: "¿Qué tono prefieres para las sugerencias?",
          gentle: "Amable y alentador",
          direct: "Directo y práctico",
          ironic: "Irónico y ligero",
          reminderTime: "¿A qué hora prefieres el recordatorio?",
          availability: "¿Cuántos minutos al día tienes para estas sugerencias?",
          minutes: "minutos"
        },
        step4: {
          title: "Preferencias",
          tone: "¿Qué tono prefieres para las sugerencias?",
          gentle: "Amable y alentador",
          direct: "Directo y práctico",
          ironic: "Irónico y ligero",
          reminderTime: "¿A qué hora prefieres el recordatorio?",
          availability: "¿Cuántos minutos al día tienes para estas sugerencias?",
          minutes: "minutos"
        },
        next: "Siguiente",
        back: "Atrás",
        finish: "Completar"
      },
      onboardingV2: {
        title: "Bienvenido",
        subtitle: "Empecemos.",
        step0: {
          title: "Hoy.",
          body: "Un pequeño gesto. Eso es suficiente."
        },
        step1: {
          title: "¿Quién está en casa?",
          micro: "Puedes cambiar esto en cualquier momento.",
          none: "Solo nosotros",
          young: "Niños pequeños",
          older: "Niños mayores",
          mixed: "Una mezcla"
        },
        step2: {
          title: "¿En qué debería enfocarse esto?",
          micro: "No es un compromiso. Solo una dirección.",
          partner: "Mi pareja",
          kids: "Mis hijos",
          both: "Ambos"
        },
        step3: {
          title: "¿Cuánto tiempo tienes realístamente la mayoría de los días?",
          micro: "Sé honesto.",
          five: "5 minutos",
          ten: "10 minutos",
          twenty: "20 minutos"
        },
        step4: {
          title: "¿Quieres un recordatorio?",
          yes: "Sí, cuando ayude",
          no: "No, abriré la app",
          micro: "Sin presión de ninguna manera."
        },
        step5: {
          title: "¿Quieres agregar su nombre?",
          placeholder: "Nombre (opcional)",
          micro: "Esto es privado."
        },
        stepTension: {
          title: "¿Qué genera más tensión?",
          micro: "Elige hasta 2. Solo nos ayuda a entender.",
          communication: "Comunicación",
          time_together: "Tiempo juntos",
          home_organization: "Hogar / Organización",
          children: "Hijos",
          intimacy: "Intimidad",
          work_stress: "Trabajo / Estrés"
        },
        finish: "Empezar",
        next: "Continuar",
        transition: "Estamos configurando tu experiencia…"
      },
      dashboard: {
        title: "Hoy",
        loading: "Generando sugerencia...",
        mainSuggestion: "Sugerencia de hoy",
        microAction: "Micro-acción rápida",
        estimatedTime: "Tiempo estimado",
        minutes: "min",
        done: "Hecho",
        postponed: "Después",
        notDone: "Omitir",
        later: "Después",
        skip: "Siguiente",
        next: "Siguiente",
        feedbackSuccess: "¡Gracias por tu opinión!",
        newSuggestion: "¡Nueva sugerencia generada!",
        addNotes: "Agregar notas (opcional)",
        notesTitle: "Notas",
        notesSubtitle: "Agrega notas sobre cómo fue",
        notesPlaceholder: "p.ej. Mencionó que le gustaría ir al teatro, le gustaría leer un libro...",
        complete: "Completar",
        cancel: "Cancelar",
        testModeTitle: "Modo prueba: Generar nueva sugerencia",
        noSuggestionYet: "Sugerencia aún no disponible",
        suggestionComesAt: "Se generará una nueva sugerencia a la hora de tu recordatorio.",
        generateManually: "Generar manualmente",
        premiumLocked: {
          noSuggestions: "No hay sugerencias",
          cta: "Suscríbete para desbloquear tu plan"
        },
        congratulations: [
          "¡Eso es todo por hoy! ¡Eres increíble! 🌟",
          "¡Fantástico! ¡Sigue así! 💪",
          "¡Buen trabajo! ¡Vas por buen camino! 🎯",
          "¡Excelente! Cada pequeño gesto cuenta ❤️",
          "¡Perfecto! Ella definitivamente lo apreciará 💝",
          "¡Genial! ¡Estos gestos hacen crecer el amor! 🌱",
          "¡Felicitaciones! ¡Estás construyendo algo hermoso! 🏆",
          "¡Bien hecho! La consistencia es la clave 🔑",
          "¡Sobresaliente! Cada día eres mejor esposo 🌈"
        ],
        category: {
          husband: "Esposo",
          father: "Padre",
          partner: "Pareja"
        },
        difficulty: {
          low: "Fácil",
          medium: "Medio",
          high: "Desafiante"
        },
        context: {
          anytime: "Cualquier momento",
          morning: "Mañana",
          afternoon: "Tarde",
          evening: "Noche"
        },
        alternative: {
          title: "Alternativa",
          question: "¿Necesitas algo diferente?",
          description: "Puedo sugerirte otra idea personalizada para hoy.",
          noThanks: "No, gracias",
          seeAlternative: "Ver una alternativa",
          generating1: "De acuerdo. Buscando tu próxima acción...",
          generating2: "Analizando tus preferencias...",
          generating3: "Generando una alternativa personalizada...",
          generating4: "¡Casi listo!",
          success: "¡Aquí hay otra idea!",
          error: "Error al cargar la alternativa"
        },
        alternativePaywall: {
          title: "No todos los días son iguales.",
          description: "Con Premium puedes adaptar el gesto al momento en que estás.",
          cta: "Activar Precisión",
          continue: "Continuar así"
        },
        skip: {
          notToday: "Hoy no",
          dontFeelLikeIt: "No tengo ganas",
          revert: "Hacerlo de todas formas",
          banner5: "Llevas 5 días omitiendo. Hoy es una buena oportunidad.",
          banner10: "No se necesita mucho. Solo un gesto."
        },
        precision: {
          title: "¿Necesitas algo más adecuado?",
          description: "Con Premium, el gesto se adapta a tu día.",
          cta: "Activar Precisión",
          close: "Cerrar"
        },
        premiumPrecision: {
          title: "Mejor, no más.",
          description: "Premium adapta la sugerencia a tu energía, estrés y rutina.",
          cta: "Activar Precisión",
          close: "Ahora no"
        },
        completion: {
          phrases: [
            "¡Bien hecho! Hiciste una diferencia hoy 💚",
            "¡Increíble! Un gesto que importa 🌱",
            "¡Lo lograste! Estás creciendo 🌳",
            "Perfecto. Ella lo notará ✨",
            "Cada gesto te acerca más 💪",
            "Hermoso. ¡Sigue así! 🔥",
            "¡Hecho! Tu jardín está creciendo 🌿",
            "Te presentaste. Eso es todo. 💚"
          ],
          addNote: "Agregar nota",
          noteTitle: "Nota",
          noteSubtitle: "Una línea, si quieres.",
          notePlaceholder: "Escribe una nota...",
          noteClose: "Cerrar",
          noteSave: "Guardar",
          error: "Error. Inténtalo de nuevo.",
          slowConnection: "Conexión lenta"
        },
        forest: {
          label: "Tu bosque"
        }
      },
      profile: {
        title: "Tú",
        settings: "Configuración",
        history: "Historial",
        language: "Idioma",
        reminderTime: "Hora del recordatorio",
        tone: "Tono de las sugerencias",
        save: "Guardar",
        saved: "¡Perfil actualizado!",
        personalInfo: "Información personal",
        name: "Nombre",
        email: "Correo electrónico",
        birthDate: "Fecha de nacimiento",
        timezone: "Zona horaria",
        yourSituation: "Tu situación",
        homeMembers: "Quién está en casa",
        whatMatters: "Qué importa más",
        timeAvailable: "Tiempo disponible",
        edit: "Editar",
        editYourData: "Editar tus datos",
        updateYourProfile: "Actualizar tu perfil",
        back: "Atrás",
        kids_cluster: {
          none: "Solo nosotros",
          young: "Niños pequeños",
          older: "Niños mayores",
          mixed: "Una mezcla"
        },
        focus: {
          partner: "Mi pareja",
          self: "Yo mismo",
          family: "Familia"
        },
        relationship: {
          cohabitating: "Conviviendo",
          married: "Casados"
        }
      },
      history: {
        title: "Historial",
        empty: "Aún no hay sugerencias",
        emptyEncouragement: "Todo viaje comienza con un pequeño paso.",
        emptyIcon: "trending-up",
        completed: "Completadas",
        total: "Total",
        status: {
          done: "Hecho",
          postponed: "Pospuesto",
          not_done: "No hecho",
          pending: "Pendiente"
        }
      },
      auth: {
        login: "Iniciar sesión",
        register: "Registrarse",
        loginButton: "Iniciar sesión",
        registerButton: "Crear cuenta",
        name: "Nombre",
        namePlaceholder: "Tu nombre",
        emailPlaceholder: "tu@correo.com",
        tagline: "Pequeñas acciones diarias. Grandes diferencias en tu relación.",
        loginSuccess: "¡Sesión iniciada correctamente!",
        registerSuccess: "¡Cuenta creada correctamente!",
        loginError: "Correo o contraseña inválidos",
        registerError: "Error en el registro",
        noAccount: "¿No tienes una cuenta? Regístrate arriba",
        hasAccount: "¿Ya tienes una cuenta? Inicia sesión arriba"
      },
      nav: {
        home: "Inicio",
        profile: "Tú",
        history: "Historial",
        events: "Eventos",
        stats: "Presencia",
        notes: "Notas"
      },
      events: {
        title: "Eventos especiales",
        eventAdded: "¡Evento agregado!",
        eventDeleted: "Evento eliminado",
        eventType: "Tipo de evento",
        anniversary: "Aniversario",
        birthday: "Cumpleaños",
        vacation: "Vacaciones",
        custom: "Personalizado",
        eventTitle: "Título",
        titlePlaceholder: "p.ej. Aniversario de bodas",
        eventDate: "Fecha",
        date: "Fecha",
        recurring: "Repetir",
        recurrence_none: "Sin repetición",
        recurrence_daily: "Diario",
        recurrence_weekly: "Semanal",
        recurrence_monthly: "Mensual",
        recurrence_yearly: "Anual",
        notes: "Notas",
        save: "Guardar",
        cancel: "Cancelar",
        empty: "Aún no hay eventos",
        emptyEncouragement: "Los momentos importantes no siempre se anuncian.",
        emptyIcon: "calendar",
        today: "Hoy",
        back: "Atrás",
        eventUpdated: "Evento actualizado"
      },
      stats: {
        title: "Presencia",
        days: "días",
        present: "Presente.",
        lastDays: "Últimos {{count}} días",
        daysOfPresence: "{{count}} / {{total}} días presente",
        presenceDescription: "Cuenta solo cuando marcas Hecho.",
        timeline: "Línea de tiempo",
        done: "Hecho",
        skipped: "Omitido",
        missed: "Perdido",
        categories: "Dónde te presentaste",
        timeInvested: "Tiempo invertido",
        hoursTotal: "horas en total",
        notes: "Notas",
        precision: "Precisión",
        categoryBreakdown: "Desglose por categoría",
        timeMetrics: "Métricas de tiempo",
        avgMinutes: "Minutos promedio",
        weeklyAvg: "Promedio semanal",
        consistencyScore: "Puntuación de consistencia",
        activeAdaptation: "Adaptación activa",
        adaptationText: "Las noches funcionan mejor para ti.\nCompletas más gestos cortos.",
        timeSection: "Tiempo",
        timeDescription: "~{{hours}}h en los últimos {{days}} días",
        overview: "RESUMEN",
        starting: "Comenzando.",
        growing: "Creciendo.",
        reliable: "Confiable.",
        unwavering: "Inquebrantable.",
        dotCaption: "Cada punto es una elección. Cada elección construye presencia.",
        gesturesMade: "{{count}} gestos realizados.",
        dotDone: "HECHO",
        dotSkipped: "OMITIDO",
        dotEmpty: "—",
        dotNoDetail: "Gesto completado.",
        dotSkippedDetail: "Omitiste este día.",
        dotEmptyDetail: "Sin gesto este día.",
        streakLegendary: "Legendario",
        streakChampion: "Campeón",
        streakOnFire: "En llamas",
        streakExcellent: "Excelente",
        streakStart: "Comenzando",
      },
      metrics: {
        title: "Métricas",
        week: "Semana",
        month: "Mes",
        all: "Todo",
        engagementScore: "Puntuación de compromiso",
        completionRate: "Tasa de completación",
        currentStreak: "Racha actual",
        best: "Mejor",
        actionsDone: "Acciones realizadas",
        assigned: "asignadas",
        timeInvested: "Tiempo invertido",
        checkinsAndMood: "Check-ins y estado de ánimo",
        totalCheckins: "Check-ins totales",
        avgMood: "Estado de ánimo promedio",
        moodTrend: "Tendencia del ánimo",
        byCategory: "Por categoría",
        weeklyBreakdown: "Desglose semanal",
        skippedActions: "Acciones omitidas",
        skippedDesc: "Sin preocupaciones, cada día es un nuevo comienzo",
        noData: "Aún no hay datos",
      },
      categories: {
        presence: "Presencia",
        repair: "Hogar",
        logistics: "Logística",
        connection: "Conexión",
        play: "Hijos",
        change_of_scene: "Cambio de escenario",
        other: "Otro",
        husband: "Esposo",
        father: "Padre"
      },
      menu: {
        title: "Menú",
        profile: "Tú",
        game: "Husband Simulator (juego)",
        metrics: "Métricas",
        settings: "Configuración",
        subscription: "Suscripción",
        logout: "Cerrar sesión",
        privacy: "Privacidad",
        terms: "Términos y condiciones",
        user: "Tú"
      },
      notes: {
        title: "Notas",
        search: "Buscar notas...",
        newNote: "Nueva nota",
        newReflection: "Nueva reflexión",
        journeyTitle: "El camino hasta aquí.",
        journeySubtitle: "Cada nota es un paso hacia más presencia y claridad.",
        today: "HOY",
        daysAgo: "HACE {{count}} DÍA",
        daysAgo_plural: "HACE {{count}} DÍAS",
        titlePlaceholder: "Título",
        contentPlaceholder: "Escribe tu nota aquí...",
        cancel: "Cancelar",
        create: "Crear",
        save: "Guardar",
        back: "Atrás",
        noContent: "Sin contenido",
        empty: "Aún no hay notas. ¡Crea tu primera nota!",
        emptyEncouragement: "Algunos momentos merecen ser recordados.",
        emptyIcon: "book-open",
        noResults: "No se encontraron resultados",
        created: "¡Nota creada!",
        updated: "¡Nota actualizada!",
        deleted: "¡Nota eliminada!",
        error: "Error durante la operación",
        titleRequired: "El título es obligatorio",
        lastUpdated: "Última actualización"
      },
      settings: {
        title: "Configuración",
        language: "Idioma",
        notifications: "Notificaciones",
        notificationsDesc: "Recibir recordatorios diarios",
        reminderTime: "Hora del recordatorio",
        adminProgQuestions: "Preguntas progresivas",
        adminProgQuestionsClicked: "Prueba de administrador activada",
        saved: "¡Configuración guardada!",
        error: "Error al guardar la configuración",
        pushNotifications: "Notificaciones push",
        pushEnabledDesc: "Recibirás notificaciones incluso cuando la app esté cerrada",
        pushDisabledDesc: "Activa para recibir recordatorios",
        pushDeniedDesc: "Permiso denegado. Activa desde la configuración del navegador",
        pushEnabled: "¡Notificaciones push activadas!",
        pushDisabled: "Notificaciones push desactivadas",
        pushDenied: "Permiso de notificaciones denegado",
        pushManagedNote: "Las notificaciones push son gestionadas por tu dispositivo — usa la configuración de tu teléfono para controlarlas.",
        progressive: {
          modalTitle: "Pregunta rápida",
          partner_name: {
            title: "¿Quieres agregar su nombre?",
            placeholder: "Nombre (opcional)"
          },
          number_of_children: {
            title: "¿Cuántos hijos tienes?",
            options: {
              "1": "1",
              "2": "2",
              "3": "3",
              "4+": "4+"
            }
          },
          schedule_type: {
            title: "¿Cómo son tus días últimamente?",
            options: {
              predictable: "Predecibles",
              chaotic: "Caóticos",
              shifts: "Por turnos"
            }
          },
          together_time: {
            title: "Últimamente, ¿tienen tiempo juntos?",
            options: {
              low: "Poco",
              medium: "Medio",
              high: "Mucho"
            }
          },
          home_load: {
            title: "En casa, ¿quién lleva más carga?",
            options: {
              me: "Yo",
              partner: "Pareja",
              even: "Equilibrado",
              varies: "Varía"
            }
          },
          focus_tension: {
            title: "Si tuvieras que elegir un área para manejar mejor este mes:",
            options: {
              communication: "Comunicación",
              time_together: "Tiempo juntos",
              home_logistics: "Hogar / Logística",
              work_stress: "Trabajo / Estrés",
              intimacy: "Intimidad",
              parenting: "Crianza"
            }
          }
        },
        dailyReminder: "Recordatorio diario",
        dailyReminderDesc: "Recibe notificación con la sugerencia del día",
        eventReminders: "Recordatorios de eventos",
        eventRemindersDesc: "Recibe notificaciones para eventos especiales",
        checkinTime: "Hora del check-in nocturno",
        checkinTimeDesc: "Cuándo recibir el recordatorio del check-in",
        preferences: "Preferencias",
        tone: "Tono de las sugerencias",
        toneGentle: "Amable",
        toneDirect: "Directo",
        toneIronic: "Irónico",
        availability: "Tiempo disponible",
        minutesDay: "min/día",
        partner: "Pareja",
        partnerName: "Nombre de la pareja",
        partnerNamePlaceholder: "p.ej. María, Juan...",
        account: "Cuenta",
        editProfile: "Editar perfil",
        subscription: "Suscripción",
        freePlan: "Plan gratuito",
        dangerZone: "Zona de peligro",
        deleteAccount: {
          title: "Eliminar cuenta",
          description: "Esta acción es permanente e irreversible",
          confirmMessage: "¿Estás seguro? Todos tus datos serán eliminados permanentemente.",
          confirm: "Eliminar",
          cancel: "Cancelar",
          deleting: "Eliminando...",
          success: "Cuenta eliminada correctamente",
          error: "Error al eliminar la cuenta"
        },
        about: "Acerca de",
        privacy: "Privacidad",
        terms: "Términos y condiciones",
        version: "Versión"
      },
      subscription: {
        title: "Suscripción",
        back: "Atrás",
        returnedFromPortal: "¡Bienvenido de vuelta!",
        free: "Gratuito",
        premium: "Premium",
        freePrice: "Gratis",
        noSubscription: "Sin suscripción",
        noSubscriptionDesc: "Para acceder a las funciones de la app se requiere el acceso de por vida",
        inactive: "Inactivo",
        dismiss: "Cerrar",
        dismissRequiredAccess: "Cerrar pantalla de suscripción",
        subscribe: "Suscribirse",
        startTrial: "Iniciar prueba gratuita de 7 días",
        recommended: "Recomendado",
        current: "Actual",
        currentPlan: "Plan actual",
        upgrade: "Activar Precisión",
        management: "Gestión",
        managePayment: "Gestionar pago",
        managePaymentDesc: "Actualizar método de pago",
        billingHistory: "Historial de facturación",
        billingHistoryDesc: "Ver facturas anteriores",
        cancelSubscription: "Cancelar suscripción",
        cancelDesc: "Cancelar renovación automática",
        infoTitle: "Información",
        infoPremium: "Tu suscripción se renueva automáticamente. Puedes cancelar en cualquier momento.",
        infoFree: "Para acceder a las funciones de la app se requiere el acceso de por vida. Un solo pago y es tuyo para siempre.",
        managingRedirect: "Abriendo portal de gestión...",
        errorPortal: "Error al abrir el portal de gestión",
        noCustomerId: "Suscripción no gestionable. Contacta soporte.",
        manualPremiumTitle: "Suscripción Premium activa",
        manualPremiumDesc: "Tu cuenta es Premium. Gestiona tu suscripción, cambia de plan o cancela directamente desde la tienda donde la compraste.",
        nextBilling: "Próxima facturación",
        trialEndsOn: "La prueba termina el",
        manageOnGoogle: "Gestionar en Google Play",
        manageOnGoogleDesc: "Abre Google Play › Suscripciones",
        manageOnStripe: "Gestionar suscripción",
        manageOnStripeDesc: "Abre el portal de facturación para actualizar el pago o cancelar",
        manageOnApple: "Gestionar en Suscripciones de Apple",
        manageOnAppleDesc: "Abre Configuración › ID de Apple › Suscripciones",
        storeManagedDescGoogle: "Tu suscripción es facturada por Google Play. Cambia de plan, actualiza el pago o cancela desde tu cuenta de Google.",
        storeManagedDescApple: "Tu suscripción es facturada por Apple. Cambia de plan, actualiza el pago o cancela desde tu cuenta de Apple.",
        storeManagedDescWeb: "Si te suscribiste en la web, gestiona tu suscripción aquí abajo. Si te suscribiste desde la app móvil, usa Google Play o el App Store en tu teléfono.",
        webNotAvailable: "Las suscripciones solo están disponibles en la app móvil BetterHusband. Por favor instálala desde App Store o Google Play.",
        feature1Free: "1 acción por día",
        feature2Free: "Notas de pareja",
        feature3Free: "Recordatorios de eventos especiales",
        feature1Premium: "Contexto profundo y aprendizaje progresivo",
        feature2Premium: "Acción alternativa",
        feature3Premium: "Check-in nocturno"
      },
      privacy: {
        title: "Privacidad",
        heading: "Política de privacidad",
        lastUpdated: "Última actualización: diciembre de 2024",
        section1Title: "Recopilación de datos",
        section1Content: "Solo recopilamos los datos necesarios para brindarte sugerencias personalizadas. Tus datos están cifrados y protegidos.",
        section2Title: "Uso de datos",
        section2Content: "Tus datos se utilizan exclusivamente para generar sugerencias personalizadas y mejorar tu experiencia.",
        section3Title: "Tus derechos",
        section3Content: "Puedes solicitar la eliminación de tus datos en cualquier momento contactándonos."
      },
      terms: {
        title: "Términos y condiciones",
        heading: "Términos de servicio",
        lastUpdated: "Última actualización: diciembre de 2024",
        section1Title: "Aceptación",
        section1Content: "Al usar esta app, aceptas estos términos de servicio.",
        section2Title: "Uso del servicio",
        section2Content: "La app está destinada al uso personal para mejorar las relaciones de pareja a través de sugerencias prácticas.",
        section3Title: "Limitaciones",
        section3Content: "Las sugerencias proporcionadas son de carácter informativo y no reemplazan el asesoramiento profesional."
      },
      checkin: {
        title: "Check-in nocturno",
        didYouComplete: "¿Completaste la sugerencia de hoy?",
        yes: "Sí, ¡hecho!",
        no: "Hoy no",
        howDoYouFeel: "¿Cómo te sientes esta noche?",
        moodSubtitle: "Tu estado de ánimo general",
        moodVeryLow: "Muy mal",
        moodLow: "Un poco bajo",
        moodNeutral: "Regular",
        moodGood: "Bien",
        moodGreat: "¡Genial!",
        howIsRelationship: "¿Cómo va la relación?",
        relationshipSubtitle: "Tu percepción hoy",
        relVeryLow: "Muy difícil",
        relLow: "Un poco tensa",
        relNeutral: "Normal",
        relGood: "Bien",
        relGreat: "¡Muy bien!",
        reflections: "Reflexiones nocturnas",
        optionalFields: "Estos campos son opcionales",
        reflection: "¿Cómo fue hoy?",
        reflectionPlaceholder: "¿Qué sucedió hoy que fue significativo en tu relación?",
        gratitude: "Gratitud",
        gratitudePlaceholder: "¿Por qué estás agradecido hoy en relación a tu pareja?",
        intention: "Intención para mañana",
        intentionPlaceholder: "¿Qué te gustaría hacer mañana por la relación?",
        back: "Atrás",
        next: "Siguiente",
        complete: "Completar",
        saved: "¡Check-in guardado! Buenas noches 🌙",
        error: "Error al guardar",
        eveningReminder: "Check-in nocturno",
        reminderText: "Tómate un momento para reflexionar sobre el día",
        alreadyDone: "¡Completado!"
      },
      notifications: {
        title: "Notificaciones",
        new: "nueva",
        empty: "Sin notificaciones",
        dailyReminder: "Recordatorio diario",
        dailyReminderMsg: "¡Tienes una sugerencia para completar hoy!",
        eveningCheckin: "Check-in nocturno",
        eveningCheckinMsg: "Es momento de reflexionar sobre el día",
        eventToday: "¡Evento hoy! 🎉",
        eventTomorrow: "Evento mañana ⏰",
        eventWeek: "Evento en 7 días 📅",
        event15Days: "Evento en 15 días 🗓️",
        pushNudgeTitle: "No te pierdas tus recordatorios",
        pushNudgeMsg: "Activa las notificaciones para recibir tus gestos en el momento justo.",
        pushModalTitle: "Mantente en camino",
        pushModalBody: "Los pequeños gestos son fáciles de olvidar. Un recordatorio amable en el momento justo te ayuda a presentarte — incluso en los días ajetreados.",
        pushModalSubtext: "Solo enviamos lo que importa. Sin spam, nunca.",
        pushModalCta: "Activar notificaciones",
        pushModalDismiss: "Ahora no",
        phrases: {
          neutral: [
            "El gesto de hoy está listo.",
            "Un momento para estar presente.",
            "Esto importa hoy.",
            "Un pequeño gesto. Bien hecho.",
            "Este es tu momento.",
            "Toma este espacio.",
            "Mejor pequeño. Pero real.",
            "Presencia, hoy.",
            "Hay una cosa que importa.",
            "Solo un gesto."
          ],
          morning: [
            "Empieza aquí.",
            "Lo primero: presencia.",
            "Hoy puedes hacer esto.",
            "Un paso. Luego el resto.",
            "No se necesita mucho.",
            "Pon esto en el centro."
          ],
          evening: [
            "Todavía hay tiempo.",
            "Un gesto antes de cerrar el día.",
            "Cierra el día así.",
            "Mejor ahora que nunca.",
            "Un último espacio para estar presente."
          ],
          identity: [
            "Ser esposo es práctica.",
            "Construye presencia.",
            "La consistencia genera confianza.",
            "En silencio.",
            "Hoy también lo haces.",
            "No para aparentar. Para estar presente."
          ],
          premium: [
            "Diseñado para hoy.",
            "Adaptado a tu momento.",
            "Simple. Al alcance.",
            "Esto es útil hoy.",
            "Te tiene en cuenta.",
            "Alineado con tu día."
          ],
          minimal: [
            "Presente.",
            "Hoy.",
            "Aquí.",
            "Esto.",
            "Ahora.",
            "Cuenta."
          ]
        }
      },
      premium: {
        title: "Hazte Premium",
        subtitle: "Desbloquea todas las funciones para tu relación",
        heroTitle: "Sé más preciso.",
        heroSubtitle: "El gesto correcto, cuando importa.",
        perMonth: "mes",
        perYear: "año",
        monthlyLabel: "Mensual",
        yearlyLabel: "Anual",
        saveLabel: "-50%",
        pricingNote: "Menos de un café a la semana.",
        featureNotes: "Notas de pareja ilimitadas",
        featureEvents: "Eventos especiales y recordatorios",
        featureReminders: "Recordatorios inteligentes de eventos",
        featureAI: "Sugerencias de IA prioritarias",
        freeLabel: "Gratis",
        premiumLabel: "Premium",
        suggestionPerDay: "sugerencia por día",
        basicNotes: "Notas básicas",
        unlimitedSuggestions: "Sugerencias ilimitadas",
        advancedNotes: "Notas avanzadas y recordatorios",
        freeActionPerDay: "1 acción por día",
        freeNotes: "Notas de pareja",
        freeEventsReminders: "Recordatorios de eventos especiales",
        premiumContext: "Contexto profundo y aprendizaje progresivo",
        premiumAlternative: "Acción alternativa",
        premiumCheckin: "Check-in nocturno",
        adaptiveTitle: "Precisión adaptativa",
        adaptiveCopy: "El motor aprende tu contexto — energía, hábitos, hijos — y personaliza cada gesto con el tiempo.",
        activatePrecision: "Desbloquear de por vida",
        ctaMicrocopy: "Un solo pago. Sin suscripción, sin renovaciones.",
        // ── Acceso de por vida (pago único) ──
        lifetimeLabel: "De por vida",
        lifetimeTagline: "Paga una vez. Es tuyo para siempre.",
        lifetimeOneTime: "Pago único",
        lifetimeOwned: "Tienes acceso de por vida",
        lifetimeOwnedDesc: "Nada que renovar, nada que cancelar.",
        // Microcopia subordinada del cobro — siempre más pequeña que el importe
        // facturado (App Store Guideline 3.1.2(c)).
        billingTerms: "Pago único de {{price}}. Sin suscripción, sin renovación automática.",
        includesTitle: "Incluye",
        upgradeNow: "Activar Premium",
        maybeLater: "Quizás después",
        securePayment: "Pago seguro con Stripe",
        errorCheckout: "Error al crear la sesión de pago",
        checking: "Verificando pago...",
        pleaseWait: "Por favor espera mientras verificamos tu pago",
        confirmingPayment: "Confirmando pago...",
        confirmingPaymentHint: "Estamos confirmando tu suscripción. Quédate aquí un momento.",
        activating: "Activando tu perfil...",
        activatingShort: "Activando...",
        activatingHint: "Un momento mientras confirmamos que tu suscripción está activa.",
        activationTimeout: "Todavía estamos confirmando tu suscripción. Toca Actualizar para reintentar.",
        refresh: "Actualizar",
        paymentConfirmed: "¡Pago confirmado!",
        subscriptionActive: "Tu suscripción está activa.",
        stillConfirming: "Seguimos confirmando el pago",
        stillConfirmingHint: "El pago fue recibido. Toca Actualizar en unos segundos para completar la activación.",
        successTitle: "¡Bienvenido a Premium!",
        nowPremium: "Ahora eres Premium",
        successMessage: "Todas las funciones premium están desbloqueadas",
        goToDashboard: "Ir al inicio",
        downloadApp: "Descargar la app",
        errorTitle: "Error de pago",
        errorMessage: "Hubo un problema al verificar tu pago. Por favor intenta de nuevo.",
        backToDashboard: "Volver al inicio",
        backToHome: "Volver al inicio",
        cancelTitle: "Pago cancelado",
        cancelMessage: "Sin problemas, puedes mejorar tu plan en cualquier momento.",
        tryAgain: "Intentar de nuevo"
      },
      downloadApp: {
        title: "Descarga Better Husband",
        subtitle: "Tu suscripción está activa. Descarga la app para empezar.",
        backToHome: "Volver al inicio"
      },
      guide: {
        menuLabel: "Guía",
        title: "Cómo funciona",
        subtitle: "GUÍA COMPLETA",
        heroTitle: "Todo lo que necesitas saber sobre BetterHusband",
        heroDescription: "Una guía completa para entender cómo la app te ayuda a estar más presente en tu relación, un pequeño gesto a la vez.",
        whatIs: {
          title: "¿Qué es BetterHusband?",
          p1: "BetterHusband es una app que te ayuda a convertirte en un esposo y padre más presente y atento. Cada día recibes una sugerencia personalizada — una micro-acción concreta calibrada a tu vida, tu relación y el tiempo que tienes disponible.",
          p2: "La app no juzga ni te dice que lo estás haciendo mal. Te guía, un pequeño gesto a la vez, para convertir las buenas intenciones en hábitos reales.",
          tip: "La consistencia importa más que la perfección. Incluso un pequeño gesto, hecho con presencia, marca la diferencia."
        },
        gettingStarted: {
          title: "Primeros pasos",
          p1: "Después de crear tu cuenta (con Google o correo electrónico), completarás un breve onboarding de 3 pasos:",
          step1Title: "Situación familiar",
          step1Desc: "¿Tienes hijos? ¿Cuántos años tienen? ¿Solo son los dos?",
          step2Title: "Tu enfoque",
          step2Desc: "¿En quién quieres enfocarte: pareja, hijos o ambos?",
          step3Title: "Tiempo disponible",
          step3Desc: "¿Cuánto tiempo puedes dedicar cada día? 5, 10 o 20 minutos.",
          tip: "Puedes cambiar estas preferencias en cualquier momento desde tu perfil."
        },
        dashboard: {
          title: "Inicio — Tu gesto diario",
          p1: "Cada día, el Inicio te muestra una sola acción personalizada — tu gesto del día. Incluye una descripción, tiempo estimado y dificultad. Tienes tres opciones:",
          doneLabel: "Hecho",
          doneDesc: "Marca el gesto como completado. ¡Celebra con confeti! 🎉",
          skipLabel: "Omitir",
          skipDesc: "¿No es tu día? Sin problema. Puedes omitir sin juicio.",
          altLabel: "Alternativa",
          altDesc: "Solicita una sugerencia diferente si la de hoy no se siente bien.",
          p2: "La tarjeta también muestra etiquetas de contexto (tiempo estimado, categoría, para quién es) para ayudarte a decidir en un segundo.",
          tip: "Toca la tarjeta para ver los detalles completos de la sugerencia, incluyendo consejos sobre cómo llevarla a cabo."
        },
        forest: {
          title: "El Bosque de Presencia",
          p1: "Cada gesto completado planta una semilla en tu bosque virtual. Cuanto más consistente seas, más crecerá y florecerá tu bosque — es una representación visual de tu compromiso.",
          p2: "Niveles del bosque:",
          level1: "Semilla — Acabas de empezar",
          level2: "Brote — Estás creciendo",
          level3: "Árbol — Eres confiable",
          level4: "Bosque — Eres consistente y presente"
        },
        checkin: {
          title: "Check-in Nocturno",
          p1: "Cada noche, puedes hacer un breve check-in guiado en 4 pasos para reflexionar sobre tu día:",
          step1: "¿Completaste la sugerencia de hoy?",
          step2: "¿Cómo te sientes? (escala de ánimo con emoji)",
          step3: "¿Cómo está la relación? (escala de relación con emoji)",
          step4: "Reflexión libre, gratitud e intención para mañana",
          tip: "El check-in nocturno es una función Premium. Solo toma 2 minutos y te ayuda a mantener la conciencia."
        },
        stats: {
          title: "Estadísticas",
          p1: "La página de estadísticas muestra tu consistencia con una cuadrícula de puntos. Cada punto representa un día:",
          dotDone: "Hecho — gesto completado",
          dotSkipped: "Omitido — pasaste ese día",
          dotEmpty: "Vacío — sin actividad",
          p2: "Puedes ver los últimos 7, 30 o 90 días. Tu nivel de presencia cambia según la consistencia:",
          levels: "Comenzando → Creciendo → Confiable → Inquebrantable"
        },
        notes: {
          title: "Notas",
          p1: "Las notas son tu diario privado. Puedes escribir reflexiones, pensamientos sobre tu relación, momentos que quieres recordar.",
          p2: "Cada nota tiene un título y contenido libre. Puedes crearlas, editarlas y eliminarlas en cualquier momento.",
          tip: "Escribe aunque sea una sola oración. Algunos momentos merecen ser recordados."
        },
        events: {
          title: "Eventos",
          p1: "Registra fechas importantes para tu relación y tu familia. Tipos admitidos:",
          type1: "Aniversarios",
          type2: "Cumpleaños",
          type3: "Vacaciones",
          type4: "Eventos personalizados",
          p2: "Los eventos pueden ser recurrentes (anuales) y la app los usa para personalizar sugerencias en días importantes.",
          tip: "Agrega al menos tu aniversario y el cumpleaños de tu pareja. La app preparará gestos especiales para esos días."
        },
        profile: {
          title: "Perfil",
          p1: "Tu perfil contiene la información que la app usa para personalizar sugerencias:",
          item1: "Nombre, correo y foto de perfil",
          item2: "Tipo de relación (conviviendo o casados)",
          item3: "Situación familiar e hijos",
          item4: "Enfoque y tiempo disponible"
        },
        settings: {
          title: "Configuración",
          p1: "En la configuración puedes ajustar:",
          item1: "Idioma de la app (Español / Inglés / Italiano)",
          item2: "Notificaciones push diarias",
          item3: "Recordatorios de eventos",
          item4: "Eliminación de cuenta"
        },
        subscription: {
          title: "Suscripción",
          p1: "BetterHusband ofrece un plan Gratuito y uno Premium:",
          free1: "1 sugerencia por día",
          free2: "Notas y diario",
          free3: "Gestión de eventos",
          prem1: "Sugerencias ilimitadas",
          prem2: "Alternativas de IA",
          prem3: "Check-in nocturno",
          prem4: "Funciones avanzadas",
          p2: "La suscripción cuesta €9,99/mes o €59,99/año (incluye prueba gratuita de 7 días). Puedes gestionarla desde la página de Suscripción."
        },
        support: {
          title: "Soporte",
          p1: "¿Necesitas ayuda o tienes una idea para mejorar la app? Desde la página de Soporte puedes:",
          item1: "Enviar una solicitud de soporte técnico",
          item2: "Sugerir una nueva función",
          p2: "Recibirás una respuesta por correo electrónico. También puedes adjuntar imágenes para explicar mejor el problema."
        },
        faq: {
          title: "Preguntas frecuentes",
          q1: "¿Cuánto tiempo toma por día?",
          a1: "De 5 a 20 minutos, según lo que elegiste durante el onboarding. Y siempre puedes cambiar tu preferencia.",
          q2: "¿La app me juzga si omito un día?",
          a2: "Nunca. No hay notificaciones pasivo-agresivas. Si omites, está bien. Mañana es un nuevo día.",
          q3: "¿Cómo se generan las sugerencias?",
          a3: "Usamos un sistema de IA que combina tu perfil, eventos, preferencias e historial para crear gestos personalizados y nunca repetitivos.",
          q4: "¿Están seguros mis datos?",
          a4: "Absolutamente. Tus datos están cifrados y nunca se comparten con terceros. Puedes eliminar tu cuenta en cualquier momento desde Configuración.",
          q5: "¿Puedo usar la app si no tengo hijos?",
          a5: "¡Por supuesto! La app se adapta a tu situación. Si no tienes hijos, las sugerencias se enfocarán en tu relación de pareja."
        }
      },
      support: {
        title: "Soporte",
        historyTitle: "Tus solicitudes",
        notAuthenticated: "Debes estar autenticado para ver el historial de solicitudes.",
        empty: "Aún no has enviado ninguna solicitud de soporte.",
        emptyDesc: "Tus solicitudes aparecerán aquí después de enviarlas.",
        typeLabel: "Tipo",
        created: "Creado el",
        status: {
          open: "Abierto",
          inProgress: "En progreso",
          closed: "Cerrado"
        },
        selectType: "¿Qué tipo de solicitud quieres hacer?",
        type: {
          support: "Soporte",
          supportTitle: "Soporte técnico",
          supportDesc: "Tienes un problema o necesitas ayuda con la app",
          feature: "Función",
          featureTitle: "Sugerir una función",
          featureDesc: "Tienes una idea para mejorar la app"
        },
        form: {
          supportTitle: "Solicitud de soporte",
          featureTitle: "Sugerir función",
          notAuthenticated: "Debes estar autenticado para enviar solicitudes de soporte.",
          subject: "Asunto",
          subjectSupportPlaceholder: "Describe el problema...",
          subjectFeaturePlaceholder: "Describe la función...",
          description: "Descripción",
          descriptionSupportPlaceholder: "Explica el problema o la solicitud de soporte...",
          descriptionFeaturePlaceholder: "Explica la nueva función o idea...",
          email: "Correo de respuesta",
          emailPlaceholder: "Tu correo electrónico",
          images: "Imágenes (opcional)",
          dragImages: "Arrastra imágenes aquí o",
          selectImages: "haz clic para seleccionar",
          preview: "Vista previa de imágenes cargadas:",
          uploading: "Cargando imágenes...",
          uploadError: "Error al cargar imágenes. Por favor intenta de nuevo.",
          sending: "Enviando...",
          send: "Enviar solicitud",
          success: "¡Solicitud enviada correctamente! Te responderemos por correo electrónico.",
          error: "Error al crear el ticket. Por favor intenta más tarde."
        },
        detail: {
          title: "Detalles de la solicitud",
          description: "Descripción",
          images: "Imágenes adjuntas",
          ticketId: "ID de solicitud",
          created: "Fecha de creación",
          updated: "Última actualización",
          email: "Correo de respuesta"
        }
      }
    }
  }
};;

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getSavedLanguage(),
    fallbackLng: 'en',
    defaultNS: 'translation',
    ns: ['common', 'translation'],
    interpolation: {
      escapeValue: false
    },
    // Debug logging
    debug: process.env.NODE_ENV === 'development'
  });

// Enhanced changeLanguage function with better error handling
const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = async (lng) => {
  try {
    console.log(`[i18n] Changing language to: ${lng}`);
    const result = await originalChangeLanguage(lng);
    
    // Save to both localStorage keys for compatibility
    if (lng === 'en' || lng === 'it' || lng === 'es') {
      localStorage.setItem('betterhusbandUserLang', lng);
      localStorage.setItem('i18nextLng', lng);
      console.log(`[i18n] Language saved to localStorage: ${lng}`);
    }
    
    return result;
  } catch (error) {
    console.error('[i18n] Error changing language:', error);
    throw error;
  }
};

// Update HTML lang, document.title and meta description when language changes
function updateHtmlMetaForLang(lng) {
  try {
    // set html lang attribute
    if (document && document.documentElement) {
      document.documentElement.lang = lng;
    }

    // update document title from translations (fallback to app name)
    const title = i18n.t('meta.title', { lng }) || 'Better Husband';
    document.title = title;

    // update meta description if present
    const desc = i18n.t('meta.description', { lng }) || '';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', desc);
  } catch (err) {
    // tests / non-browser environments may throw - ignore
    // but log in dev for visibility
    if (process.env.NODE_ENV === 'development') console.warn('i18n: failed to update HTML meta', err);
  }
}

// Add language change listener (updates meta + debug log)
i18n.on('languageChanged', (lng) => {
  console.log(`[i18n] Language changed to: ${lng}`);
  updateHtmlMetaForLang(lng);
});

// Ensure initial HTML meta reflects selected language right away
updateHtmlMetaForLang(i18n.language || 'en');

// Detect language from IP geolocation (only when user has no saved preference)
export const detectGeoLanguage = async () => {
  try {
    const hasExplicit = !!(localStorage.getItem('betterhusbandUserLang') || localStorage.getItem('i18nextLng'));
    if (hasExplicit) {
      console.log('[i18n] User has explicit language preference, skipping geo detection');
      return;
    }
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    // Timeout wrapper: if geo API takes >3s, abort and keep default language
    // (prevents blocking app launch on slow networks or backend issues)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    try {
      const res = await fetch(`${BACKEND_URL}/api/geo`, { signal: controller.signal });
      const data = await res.json();
      // A campaign URL (/onboarding/:lang or ?lang=) may have set an explicit
      // preference while this request was in flight — that intent must win over
      // IP geolocation, so re-check before applying anything.
      if (localStorage.getItem('betterhusbandUserLang')) {
        console.log('[i18n] Explicit language chosen during geo lookup, keeping it');
        return;
      }
      // Spanish-speaking countries: Spain + LATAM (excluding Brazil — Portuguese)
      const SPANISH_COUNTRIES = new Set([
        'ES', 'MX', 'AR', 'CO', 'CL', 'PE', 'VE', 'EC', 'GT', 'CU', 'BO',
        'DO', 'HN', 'PY', 'SV', 'NI', 'CR', 'PA', 'UY', 'PR'
      ]);
      if (data?.country === 'IT' && i18n.language !== 'it') {
        console.log('[i18n] Italian IP detected, switching to Italian');
        await i18n.changeLanguage('it');
      } else if (data?.country && SPANISH_COUNTRIES.has(data.country) && i18n.language !== 'es') {
        console.log(`[i18n] Spanish-speaking country detected (${data.country}), switching to Spanish`);
        await i18n.changeLanguage('es');
      }
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (err) {
    // Fallback: if backend unreachable / aborted, honor the device locale
    // so iPad reviewers in Italy still see Italian without blocking launch.
    console.warn('[i18n] Geo detection failed, falling back to navigator.language:', err);
    try {
      const navLang = (typeof navigator !== 'undefined' && (navigator.language || (navigator.languages && navigator.languages[0]))) || 'en';
      const lower = navLang.toLowerCase();
      const fallback = lower.startsWith('it') ? 'it' : lower.startsWith('es') ? 'es' : 'en';
      if (i18n.language !== fallback) {
        await i18n.changeLanguage(fallback);
      }
    } catch (_) { /* ignore */ }
  }
};

export default i18n;